import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { Button, Form, Row, Col, Alert, Modal } from "react-bootstrap";
import {
  getAccountPatients,
  getServicesByPracticeNumbers,
  getAppointmentsByPatientId,
  fetchallUsersByPractices,
  getPatientInvoiceICDs,
} from "../../api";
import {
  createInvoice,
  getInvoiceById,
  updateInvoice,
  sendInvoiceByEmail,
  updateClaim,
  getClaimByInvoiceId,
} from "../../afinanceapi";
import ICD10PickerModal from "../SearchICD10/SearchICD10";
import TariffSearch from "../Services/searchTariffs";
import NappiSearch from "../Services/searchNappi";
import { CompactMultiSelect } from "../MultiSelect/MultiSelect";
import { fetchAllmsrRates } from "../../medPracapi";
import "./CreateInvoice.css";
import EmailInvoiceModal from "./EmailInvoice";
import { useNavigate, useLocation } from "react-router-dom";
import AddModifierModal from "./Modifier";
import InvoiceAppointmentModal from "../FullCalendar/AppointmentPicker";
import AppointmentDateInput from "../FullCalendar/AppointmentDateInput";
import CasePickerPanel from "../Cases/CasePickerPanel";
import { getCasesByPatientId } from "../../acases";
import { getModifierByDisciplineCode } from "../../afinanceapi";
import { set } from "date-fns";

function CreateInvoiceModal({
  account,
  practice,
  onInvoiceUpdated,
  initialInvoice,
  claim,
  placesOfService,
  selPatientId,
}) {
  // Core state

  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(selPatientId);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);
  const [selectedICD10s, setSelectedICD10s] = useState([]);
  const [lines, setLines] = useState([]);
  const [lineAppointments, setLineAppointments] = useState({});
  const [invoiceStatus, setInvoiceStatus] = useState(
    initialInvoice?.status || "Draft"
  );
  const [invoiceNumber, setInvoiceNumber] = useState(
    initialInvoice?.invoiceNumber || ""
  );
  const [invoiceDate, setInvoiceDate] = useState(
    initialInvoice?.invoiceDate || new Date().toISOString().split("T")[0]
  );
  const [practitioners, setPractioners] = useState([]);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState(
    initialInvoice?.practitioner?.id || ""
  );
  const [invoice, setInvoice] = useState(initialInvoice || null);
  const [invoiceErrors, setInvoiceErrors] = useState({});
  const [lineErrors, setLineErrors] = useState({});
  const [selectedPlaceOfService, setSelectedPlaceOfService] = useState("");
  const [showEmailInvoiceModal, setShowEmailInvoiceModal] = useState(false);
  const [invoiceBlob, setInvoiceBlob] = useState(null);
  const [feedback, setFeedback] = useState(claim);
  const [successMessage, setSuccessMessage] = useState(false);
  const [outpatient, setOutpatient] = useState(true);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [attachedCase, setAttachedCase] = useState(null);
  const [showAddCase, setShowAddCase] = useState(false);
  const [inPatientCase, setInPatientCase] = useState(false);
  const [showModifier, setShowModifier] = useState(false);
  const [icdSelections, setIcdSelections] = useState([]);
  const [showICDModal, setShowICDModal] = useState(false);
  const [showLineApptModal, setShowLineApptModal] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [lineApptInitial, setLineApptInitial] = useState(null);
  const [cases, setCases] = React.useState([]);
  const { id } = useParams();
  const [busy, setBusy] = useState(null);
  const [modifierCatalog, setModifierCatalog] = useState(new Map());
  const [initialDisciplineCode, setInitialDisciplineCode] = useState("");

  // New: default appointment template for subsequent lines
  const [defaultAppointmentTemplate, setDefaultAppointmentTemplate] =
    useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const initialInvoiceApp = location.state?.initialInvoiceApp;
  const incomingServiceId = location.state?.servicesId;
  console.log("State on load:", location.state);

  // ======= Memoized derived values =======
  const primaryIcds = useMemo(
    () => icdSelections.filter((s) => s.isPrimary).map((s) => s.item),
    [icdSelections]
  );

  const secondaryIcds = useMemo(
    () => icdSelections.filter((s) => !s.isPrimary).map((s) => s.item),
    [icdSelections]
  );

  // Always parse using "." as decimal separator
  const normalizeToNumber = (val) => {
    if (val == null) return 0;

    const normalized = String(val)
      .replace(",", ".") // if user typed a comma, treat as "."
      .replace(/[^0-9.]/g, ""); // keep only digits and dot

    const num = parseFloat(normalized);
    return Number.isFinite(num) ? num : 0;
  };

  // Always format with "." and 2 decimals
  const formatAmount = (val) => {
    const num = normalizeToNumber(val);
    return num.toFixed(2); // JS spec: always uses "." as decimal
  };

  const totalAmount = useMemo(
    () =>
      lines.reduce((total, line) => {
        const rate = normalizeToNumber(line.rate);
        return total + rate;
      }, 0),
    [lines]
  );

  const totalMedicalAidAmount = useMemo(
    () =>
      lines.reduce((total, line) => {
        if (
          account.accountMedicalAid &&
          !line.privatePayment &&
          (line.tariffCode || line.nappiCode)
        ) {
          const rate = normalizeToNumber(line.rate);
          return total + rate;
        }
        return total;
      }, 0),
    [lines, account.accountMedicalAid]
  );

  const totalPatientAmount = useMemo(
    () =>
      lines.reduce((total, line) => {
        if (
          !account.accountMedicalAid ||
          line.privatePayment ||
          (!line.tariffCode && !line.nappiCode)
        ) {
          const rate = normalizeToNumber(line.rate);
          return total + rate;
        }
        return total;
      }, 0),
    [lines, account.accountMedicalAid]
  );

  const isPosted = invoiceStatus === "Paid" || invoiceStatus === "Posted";

  // ======= Utils (memoized) =======
  const getIcdCode = useCallback(
    (icd) => icd?.ICD10_Code || icd?.ICD10_3_Code || "",
    []
  );

  const getIcdOneLine = useCallback((icd) => {
    const groupCode = icd.Group_Code?.split(" ")[0] ?? "";
    const groupDesc = icd.Group_Desc ?? "";
    const icd3Code = icd.ICD10_3_Code ?? "";
    const icd3Desc = icd.ICD10_3_Description ?? "";
    const code = icd.ICD10_Code ?? "";
    const desc = icd.ICD10_Description ?? "";
    return `${icd3Code} - ${icd3Desc} | ${
      code || icd3Code
    } - ${desc} | ${groupCode} - ${groupDesc}`;
  }, []);

  const splitDT = useCallback((iso) => {
    if (!iso) return { date: "", time: "" };
    const [date, timeFull = ""] = String(iso).split("T");
    return { date, time: timeFull.slice(0, 5) };
  }, []);

  // ======= Appointment helpers =======
  const apptErrorFor = (idx) =>
    idx == null
      ? ""
      : lineErrors?.[idx]?.appointmentDate ||
        lineErrors?.[idx]?.appointmentId ||
        "";

  const ensureAppointmentsForPatient = useCallback(
    async (patientId) => {
      if (!patientId) return [];
      if (lineAppointments[patientId]) {
        return lineAppointments[patientId];
      }
      const appointments = await getAppointmentsByPatientId(patientId);
      setLineAppointments((prev) => ({
        ...prev,
        [patientId]: appointments,
      }));
      return appointments;
    },
    [lineAppointments]
  );

  const createDefaultTemplate = useCallback((line) => {
    if (!line) return null;
    return {
      useCustomDate: line.useCustomDate,
      appointmentId: line.appointmentId || "",
      appointmentStartSet: line.appointmentStartSet || null,
      appointmentEndSet: line.appointmentEndSet || null,
      appointmentDateSet: line.appointmentDateSet || null,
      appointmentCustomStart: line.appointmentCustomStart || null,
      appointmentCustomEnd: line.appointmentCustomEnd || null,
      appointmentDateCustom: line.appointmentDateCustom || null,
      appointmentDate: line.appointmentDate || null,
    };
  }, []);

  const buildLineApptPatch = useCallback((sel) => {
    if (!sel) return {};
    if (!sel.useCustomDate) {
      const d = sel.appointmentStartSet
        ? sel.appointmentStartSet.slice(0, 10)
        : null;
      return {
        useCustomDate: false,
        appointmentId: sel.appointmentId || "",
        appointmentStartSet: sel.appointmentStartSet || null,
        appointmentEndSet: sel.appointmentEndSet || null,
        appointmentDateSet: d,
        appointmentCustomStart: null,
        appointmentCustomEnd: null,
        appointmentDateCustom: null,
        appointmentDate: d, // legacy
      };
    }
    const d = sel.appointmentCustomStart
      ? sel.appointmentCustomStart.slice(0, 10)
      : null;
    return {
      useCustomDate: true,
      appointmentId: null,
      appointmentStartSet: null,
      appointmentEndSet: null,
      appointmentDateSet: null,
      appointmentCustomStart: sel.appointmentCustomStart || null,
      appointmentCustomEnd: sel.appointmentCustomEnd || null,
      appointmentDateCustom: d,
      appointmentDate: d, // legacy
    };
  }, []);

  const setLineFields = useCallback((idx, patch) => {
    setLines((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, ...patch } : l))
    );
  }, []);

  // Shape for AppointmentDateInput value
  const lineApptValue = useCallback(
    (line) => {
      if (!line) return null;
      if (line.useCustomDate) {
        const s = splitDT(line.appointmentCustomStart);
        const e = splitDT(line.appointmentCustomEnd);
        return {
          useCustomDate: true,
          startDate: s.date,
          startTime: s.time,
          endDate: e.date,
          endTime: e.time,
        };
      }
      const s = splitDT(
        line.appointmentStartSet ||
          (line.appointmentDateSet ? `${line.appointmentDateSet}T00:00` : "")
      );
      const e = splitDT(line.appointmentEndSet);
      return {
        useCustomDate: false,
        startDate: s.date,
        startTime: s.time,
        endDate: e.date,
        endTime: e.time,
      };
    },
    [splitDT]
  );
  // ======= Validation =======

  const runGuarded = async (key, fn) => {
    if (busy) return; // ignore double clicks
    // Only set busy **after** validations pass inside fn (see below)
    await fn(
      async () => setBusy(key),
      () => setBusy(null)
    );
  };

  // In validation, check the ref instead
  const validateInvoiceFields = useCallback(
    (currentValues = {}) => {
      const {
        invoiceDate: checkInvoiceDate = invoiceDate,
        selectedPractice: checkPractice = selectedPracticeId,
        selectedPractitionerId: checkPractitionerId = selectedPractitionerId,
        selectedPatientId: checkPatientId = selectedPatientId,
        selectedPlaceOfService: checkPlaceOfService = selectedPlaceOfService,
        icdSelections: checkIcdSelections = icdSelections,
      } = currentValues;

      const newErrors = {};

      if (!checkInvoiceDate)
        newErrors.invoiceDate = "Invoice date is required.";
      if (!checkPractice) newErrors.practice = "Practice is required.";
      if (!checkPractitionerId)
        newErrors.practitioner = "Practitioner is required.";
      if (!checkPatientId) newErrors.patient = "Patient is required.";
      if (!checkPlaceOfService)
        newErrors.placeOfService = "Place of service is required.";

      if (account.accountMedicalAid && checkIcdSelections.length === 0) {
        newErrors.icd10 =
          "At least one ICD10 code is required for medical aid patients.";
      }

      setInvoiceErrors(newErrors);
      return Object.keys(newErrors)?.length === 0;
    },
    [
      invoiceDate,
      selectedPractice,
      selectedPractitionerId,
      selectedPatientId,
      selectedPlaceOfService,
      account.accountMedicalAid,
      icdSelections,
    ]
  );

  const validateInvoiceLines = useCallback(() => {
    const errors = {};
    let hasError = false;

    if (!Array.isArray(lines) || lines.length === 0) {
      alert("At least one invoice line is required.");
      return false;
    }

    const hasVal = (v) =>
      v != null && (typeof v !== "string" || v.trim() !== "");

    lines.forEach((line, idx) => {
      const lineError = {};

      // ✅ If NOTHING has been chosen/entered yet, block immediately
      const missingAllApptFields =
        !hasVal(line.appointmentId) &&
        !hasVal(line.appointmentStartSet) &&
        !hasVal(line.appointmentDateSet) &&
        !hasVal(line.appointmentCustomStart) &&
        !hasVal(line.appointmentCustomEnd);

      if (missingAllApptFields) {
        lineError.appointmentDate =
          "Select an appointment or enter a custom start & end time.";
      } else if (line.useCustomDate) {
        // === Custom date-time path ===
        const s = line.appointmentCustomStart;
        const e = line.appointmentCustomEnd;

        if (!hasVal(s) || !hasVal(e)) {
          lineError.appointmentDate = "Start and end time are required.";
        } else {
          const ds = new Date(s);
          const de = new Date(e);
          if (Number.isNaN(ds.getTime()) || Number.isNaN(de.getTime())) {
            lineError.appointmentDate = "Invalid date/time.";
          } else if (de <= ds) {
            lineError.appointmentDate = "End time must be after start time.";
          }
        }
      } else {
        // === Linked appointment path ===
        const hasAppt =
          hasVal(line.appointmentId) ||
          hasVal(line.appointmentStartSet) ||
          hasVal(line.appointmentDateSet);

        if (!hasAppt) {
          lineError.appointmentId = "Appointment is required.";
        }
      }
      // In your validation function, add this logging inside the forEach:
      console.log(`Line ${idx}:`, {
        appointmentId: line.appointmentId,
        appointmentStartSet: line.appointmentStartSet,
        appointmentDateSet: line.appointmentDateSet,
        appointmentCustomStart: line.appointmentCustomStart,
        appointmentCustomEnd: line.appointmentCustomEnd,
        missingAllApptFields,
      });

      // === Existing checks ===
      if (
        account.accountMedicalAid &&
        !line.privatePayment &&
        !hasVal(line.icd101)
      ) {
        lineError.icd101 = "ICD10 code is required.";
      }

      if (!hasVal(line.description)) {
        lineError.description = "Description is required.";
      }

      const qty = Number(line.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        lineError.quantity = "Quantity must be greater than 0.";
      }

      const rateNum = parseFloat(String(line.rate ?? "").replace(/[, ]+/g, ""));
      if (!Number.isFinite(rateNum) || rateNum < 0) {
        lineError.rate = "Rate must be 0 or greater.";
      }

      if (!selectedPlaceOfService) {
        lineError.placeOfService = "Place of Service is required.";
      }

      if (Object.keys(lineError).length) {
        errors[idx] = lineError;
        hasError = true;
      }
    });

    setLineErrors(errors);
    return !hasError;
  }, [lines, account.accountMedicalAid, selectedPlaceOfService]);

  // ======= Generic add line with defaults =======
  const addLineWithDefaults = useCallback(
    async (type, additionalFields = {}) => {
      if (!validateInvoiceFields()) return;

      const patientId = selectedPatientId;
      const nextLineNumber = (lines?.length || 0) + 1;

      const baseLine = {
        lineNumber: nextLineNumber,
        type,
        patientId,
        icd101: primaryIcds[0] ? getIcdCode(primaryIcds[0]) : "",
        icd10Secondary: secondaryIcds.map(getIcdCode),
        oti: "A",
        quantity: 1,
        rate: "0.00",
        privatePayment: false,
        // Always start with empty appointment fields - user must select
        appointmentId: "",
        useCustomDate: false,
        appointmentStartSet: null,
        appointmentEndSet: null,
        appointmentDateSet: null,
        appointmentCustomStart: null,
        appointmentCustomEnd: null,
        appointmentDateCustom: null,
        appointmentDate: null,
        ...additionalFields,
      };

      setLines((prev) => [...prev, baseLine]);
    },
    [
      validateInvoiceFields,
      selectedPatientId,
      lines?.length,
      primaryIcds,
      secondaryIcds,
      getIcdCode,
    ]
  );

  // ======= Simplified line add handlers =======
  const handleAddLine = useCallback(() => {
    addLineWithDefaults("custom", { tariffCode: "", description: "" });
  }, [addLineWithDefaults]);

  const handleAddServiceLine = useCallback(() => {
    addLineWithDefaults("service", {
      serviceId: "",
      selectedService: null,
      tariffCode: "",
      description: "",
    });
  }, [addLineWithDefaults]);

  const handleAddTariffLine = useCallback(() => {
    addLineWithDefaults("tariff", {
      selectedTariff: null,
      tariffCode: "",
      description: "",
      practiceNumber: selectedPractice?.practiceNumber || null,
    });
  }, [addLineWithDefaults, selectedPractice?.practiceNumber]);

  const handleAddNappiLine = useCallback(() => {
    addLineWithDefaults("nappi", {
      selectedNappi: null,
      nappiCode: "",
      description: "",
    });
  }, [addLineWithDefaults]);

  // If the first line appointment changes, update template
  const handleLineAppointmentChange = useCallback(
    (index, appointmentData) => {
      setLines((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;

        // Update the edited line
        updated[index] = { ...updated[index], ...appointmentData };

        const gid = updated[index].groupId;
        if (!gid) return updated; // not a grouped service; nothing to propagate

        // Find the group's lines and the first (smallest lineNumber)
        const groupIndices = updated
          .map((l, i) => ({ i, l }))
          .filter(({ l }) => l.groupId === gid);

        if (groupIndices.length <= 1) return updated;

        const firstLineNumberInGroup = Math.min(
          ...groupIndices.map(({ l }) => l.lineNumber || 0)
        );
        const isFirstInGroup =
          (updated[index].lineNumber || 0) === firstLineNumberInGroup;

        // Only the first line auto-propagates
        if (!isFirstInGroup) return updated;

        // Build a normalized "template" from the edited line
        const patch = createDefaultTemplate(updated[index]);

        // Apply to the rest of the group (excluding the edited one)
        groupIndices.forEach(({ i: gi }) => {
          if (gi === index) return;
          updated[gi] = { ...updated[gi], ...patch };
        });

        return updated;
      });

      // Keep the global default in sync (handy for future new lines)
      setDefaultAppointmentTemplate((prevTpl) => {
        const nextTpl = {
          ...(prevTpl || {}),
          ...createDefaultTemplate({ ...lines[index], ...appointmentData }),
        };
        return nextTpl;
      });
    },
    [lines, createDefaultTemplate]
  );

  // ======= Service / tariff / nappi helpers =======
  const DEFAULT_MSR_YEAR = String(new Date().getFullYear()); // "2026" right now

  function getLineServiceDate(line, fallbackInvoiceDate) {
    // Prefer appointment-derived fields (custom > set), then legacy appointmentDate, then invoiceDate
    const d =
      line?.appointmentDateCustom ||
      line?.appointmentDateSet ||
      line?.appointmentDate ||
      fallbackInvoiceDate ||
      "";

    return String(d).slice(0, 10); // "YYYY-MM-DD"
  }

  function getYearFromYYYYMMDD(dateStr, fallbackYear = DEFAULT_MSR_YEAR) {
    const y = String(dateStr || "").slice(0, 4);
    return /^\d{4}$/.test(y) ? y : fallbackYear;
  }

  const enrichModifiers = (mods = [], catalog /* Map */) => {
    const arr = Array.isArray(mods) ? mods : [];
    return arr
      .map((m) => {
        if (!m) return null;
        const code = typeof m === "string" ? m : m.code;
        if (!code) return null;

        const meta = catalog?.get(String(code));
        return meta
          ? { code: String(code), ...meta } // ✅ now computable
          : { code: String(code) }; // fallback: code-only
      })
      .filter(Boolean);
  };

  const getMSRRate = useCallback(
    async (practiceNumber, tariffCode, planOptionCode, year) => {
      const rateData = await fetchAllmsrRates(
        practiceNumber,
        [tariffCode],
        planOptionCode,
        year
      );

      const msrEntry = rateData.find(
        (entry) => (entry.priceGroupType || "").toLowerCase() === "msr"
      );

      let rate = 0;
      let quantity = 1;

      if (msrEntry) {
        quantity = msrEntry.numberOfUnits || 1;

        if (msrEntry.tariffRandSchemeFixed != null) {
          rate = msrEntry.tariffRandSchemeFixed;
        } else if (msrEntry.tariffRandRcfSchemeRate != null) {
          rate = msrEntry.tariffRandRcfSchemeRate;
        } else if (msrEntry.tariffRandCalculated != null) {
          rate = msrEntry.tariffRandCalculated;
        }
      }

      return { rate: parseFloat(rate.toFixed(2)), quantity };
    },
    []
  );

  const normalizePct = (v) => {
    if (v == null) return 0;
    if (typeof v === "string") return parseFloat(v.replace("%", "")) || 0;
    if (typeof v === "number") return v;
    return 0;
  };

  // Build a catalog: code -> { adjustment, adjustmentPercentage }
  const buildModifierCatalog = (mods = []) => {
    const map = new Map();
    (mods || []).forEach((m) => {
      if (!m?.code) return;
      map.set(String(m.code), {
        adjustment: m.adjustment,
        adjustmentPercentage: m.adjustmentPercentage,
      });
    });
    return map;
  };

  const repriceLineFromMSR = (line, msr, modifierCatalog /* Map */) => {
    // MSR base
    const base = Number(msr?.rate ?? 0);
    const baseRate = Number.isFinite(base) ? base : 0;

    const existing = Array.isArray(line.modifier) ? line.modifier : [];
    const rebuiltModifiers = existing
      .map((m) => (typeof m === "string" ? { code: m } : m))
      .filter((m) => m?.code)
      .map((m) => {
        const code = String(m.code);

        // Option A: meta already on the line
        const adj = m.adjustment ?? modifierCatalog?.get(code)?.adjustment;
        const pctVal =
          m.adjustmentPercentage ??
          modifierCatalog?.get(code)?.adjustmentPercentage;

        const pct = normalizePct(pctVal) / 100;

        // If we still don't know how to compute, keep code only (display-only)
        if (!adj || !pct) return { ...m, code, rateChange: 0 };

        const rateChange =
          adj === "up"
            ? +(baseRate * pct).toFixed(2)
            : adj === "down"
            ? +(-baseRate * pct).toFixed(2)
            : 0;

        return {
          ...m,
          code,
          adjustment: adj,
          adjustmentPercentage: pctVal,
          rateChange,
        };
      });

    const totalChange = rebuiltModifiers.reduce(
      (sum, m) => sum + (Number(m.rateChange) || 0),
      0
    );

    const finalRate = +(baseRate + totalChange).toFixed(2);

    return {
      ...line,
      quantity: msr?.quantity ?? line.quantity ?? 1,
      originalRate: baseRate.toFixed(2), // MSR base
      rate: finalRate.toFixed(2), // base + modifiers
      modifier: rebuiltModifiers,
    };
  };

  const refreshMSRForLine = useCallback(
    async (index, appointmentPatch = null) => {
      if (!selectedPractice?.practiceNumber) return;
      if (!account?.accountMedicalAid) return;

      const currentLine = lines[index];
      if (!currentLine) return;

      // skip private or nappi
      if (currentLine.privatePayment) return;
      if (currentLine.type === "nappi" || !!currentLine.nappiCode) return;

      const tariffCode =
        currentLine.tariffCode || currentLine.selectedTariff?.code || "";
      if (!tariffCode) return;

      const merged = appointmentPatch
        ? { ...currentLine, ...appointmentPatch }
        : currentLine;

      const fallbackInvoiceDate =
        invoiceDate || new Date().toISOString().slice(0, 10);

      const lineDate = getLineServiceDate(merged, fallbackInvoiceDate);
      const msrYear = getYearFromYYYYMMDD(lineDate);

      const msr = await getMSRRate(
        selectedPractice.practiceNumber,
        tariffCode,
        account.accountMedicalAidOptionCode,
        msrYear
      );

      // Build modifier catalog for this practice/discipline OR from selectedService
      const catalog = buildModifierCatalog(
        merged?.selectedService?.pricing?.selectedModifiers || []
      );

      setLines((prev) => {
        const updated = [...prev];
        const lineNow = updated[index];
        if (!lineNow) return prev;

        // avoid overwriting if tariff changed while awaiting
        const nowTariff =
          lineNow.tariffCode || lineNow.selectedTariff?.code || "";
        if (String(nowTariff) !== String(tariffCode)) return prev;

        updated[index] = repriceLineFromMSR(lineNow, msr, catalog);
        return updated;
      });
    },
    [
      selectedPractice?.practiceNumber,
      account?.accountMedicalAid,
      account?.accountMedicalAidOptionCode,
      lines,
      invoiceDate,
      getMSRRate,
      getLineServiceDate,
      getYearFromYYYYMMDD,
    ]
  );

  const refreshMSRForGroup = useCallback(
    async (groupId, appointmentPatch) => {
      const groupIndexes = lines
        .map((l, i) => ({ l, i }))
        .filter(({ l }) => l.groupId === groupId)
        .map(({ i }) => i);

      // refresh each line (tariff lines will update; nappi/private will no-op)
      for (const i of groupIndexes) {
        await refreshMSRForLine(i, appointmentPatch);
      }
    },
    [lines, refreshMSRForLine]
  );

  const makeGroupId = () =>
    `svc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;

  const handleServiceSelect = useCallback(
    async (index, serviceId) => {
      const selectedService = services.find((s) => s._id === serviceId);
      if (!selectedService) return;
      console.log("Selected service:", selectedService);

      // --- Service modifiers (tariff lines only) ---
      const serviceModifiers = selectedService.pricing?.selectedModifiers || [];

      const isNappiLine = (l) => l?.type === "nappi" || !!l?.nappiCode;
      const isTariffLine = (l) => !isNappiLine(l);

      // Applies modifiers to a line.
      // Supports two shapes of modifier inputs:
      //
      // 1) FULL pricing modifiers (can change rate):
      //    { code: "0003", adjustment: "up"|"down", adjustmentPercentage: 10 | "10%" }
      //
      // 2) CODE-ONLY modifiers (informational only, DOES NOT change rate):
      //    { code: "0008" }   or   "0008"
      //
      // If you pass only code-only modifiers (like the ones in your selectedTariffs[].modifiers),
      // the function will attach them to line.modifier but will NOT change rate.
      const applyModifiersToLine = (line, modifiersInput = []) => {
        const modsArr = Array.isArray(modifiersInput) ? modifiersInput : [];

        // Normalize to objects: { code, adjustment?, adjustmentPercentage? }
        const normalizedMods = modsArr
          .map((m) => {
            if (!m) return null;
            if (typeof m === "string") return { code: m };
            if (typeof m === "object") return { ...m };
            return null;
          })
          .filter((m) => m && m.code);

        // Nothing to do
        if (!normalizedMods.length) return line;

        // Determine base rate (number)
        let baseRate = parseFloat(line.originalRate ?? line.rate ?? "0");
        if (Number.isNaN(baseRate)) baseRate = 0;

        // Start from existing modifiers if present
        const existingMods = Array.isArray(line.modifier)
          ? line.modifier
              .map((m) => (typeof m === "string" ? { code: m } : m))
              .filter((m) => m && m.code)
          : [];

        // We will replace/merge by code (avoid duplicates)
        const byCode = new Map();
        existingMods.forEach((m) => byCode.set(String(m.code), m));

        // Track whether we have enough info to compute rate changes
        let canComputeAny = false;
        let totalChange = 0;

        normalizedMods.forEach((mod) => {
          const code = String(mod.code);

          // Merge into map (latest wins)
          const merged = {
            ...(byCode.get(code) || {}),
            ...mod,
            code,
          };

          // See if this modifier is "computable"
          // Needs BOTH adjustment AND adjustmentPercentage
          const hasAdj =
            merged.adjustment === "up" || merged.adjustment === "down";
          let rawPct = merged.adjustmentPercentage;

          // Parse "10%" / "10" / 10
          let pctNum = 0;
          if (typeof rawPct === "string") {
            pctNum = parseFloat(rawPct.replace("%", ""));
          } else if (typeof rawPct === "number") {
            pctNum = rawPct;
          }

          const hasPct = Number.isFinite(pctNum) && pctNum !== 0;

          if (hasAdj && hasPct) {
            canComputeAny = true;

            const pct = pctNum / 100;
            let rateChange = 0;

            if (merged.adjustment === "up") rateChange = baseRate * pct;
            else if (merged.adjustment === "down") rateChange = -baseRate * pct;

            rateChange = Number(rateChange.toFixed(2));
            totalChange += rateChange;

            // Persist the computed change on the modifier object
            merged.rateChange = rateChange;
          } else {
            // Code-only or insufficient info → ensure no stale rateChange
            if ("rateChange" in merged) delete merged.rateChange;
          }

          byCode.set(code, merged);
        });

        const finalMods = Array.from(byCode.values());

        // If we can't compute any modifier impacts, don't touch pricing.
        if (!canComputeAny) {
          return {
            ...line,
            modifier: finalMods,
            // preserve originalRate/rate as-is
          };
        }

        const newRate = Number((baseRate + totalChange).toFixed(2));

        return {
          ...line,
          modifier: finalMods,
          originalRate: line.originalRate ?? baseRate.toFixed(2),
          rate: newRate.toFixed(2),
        };
      };

      // --- Group id for service-created rows ---
      const makeGroupId = () =>
        `svc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;

      const groupId = makeGroupId();

      const tariffs = selectedService.pricing?.selectedTariffs || [];
      const useFlatRate = selectedService.pricing?.paymentOptions?.useFlatRate;
      const useFlatAndMedicalAidRate =
        selectedService.pricing?.paymentOptions?.useFlatAndMedicalAidRate;

      const fallbackDescription =
        selectedService.pricing?.invoiceDescription || "";
      const fallbackFlatRateString = selectedService.pricing?.flatRate || "R 0";
      const fallbackFlatRate =
        parseFloat(fallbackFlatRateString.replace(/[^\d.]/g, "")) || 0;

      const isMedicalAid = !!account?.accountMedicalAid;

      // Keep reference to original row (for patientId + appointment fields)
      const originalLine = lines[index] || {};

      // Prefer global default, else use line template, else none
      const templateForGroup =
        defaultAppointmentTemplate ||
        createDefaultTemplate(originalLine) ||
        null;

      const withTemplate = (line) =>
        templateForGroup ? { ...line, ...templateForGroup } : line;

      // Common base fields (appts carry from original, but template may override)
      const baseLineInfoCommon = {
        groupId,
        lineNumber: originalLine.lineNumber ?? index + 1,
        patientId: originalLine.patientId || selectedPatientId || "",
        useCustomDate: !!originalLine.useCustomDate,

        appointmentId: originalLine.appointmentId || "",
        appointmentStartSet: originalLine.appointmentStartSet || null,
        appointmentEndSet: originalLine.appointmentEndSet || null,
        appointmentDateSet: originalLine.appointmentDateSet || null,
        appointmentCustomStart: originalLine.appointmentCustomStart || null,
        appointmentCustomEnd: originalLine.appointmentCustomEnd || null,
        appointmentDateCustom: originalLine.appointmentDateCustom || null,
        appointmentDate: originalLine.appointmentDate || null,

        icd101: primaryIcds[0] ? getIcdCode(primaryIcds[0]) : "",
        icd10Secondary: secondaryIcds.map(getIcdCode),
        serviceId,
        selectedService,
        type: "service",
      };

      // For year derivation: use invoiceDate fallback if needed
      const fallbackInvoiceDate =
        invoiceDate || new Date().toISOString().slice(0, 10);

      let newServiceLines = [];

      // ---------------------------
      // CASE 1:
      // tariffs exist AND (private OR (medical aid but useFlatRate && tariffs=0 ... your original logic))
      // NOTE: I kept your conditions intact, only added year handling where MSR is called.
      // ---------------------------
      if (
        (!isMedicalAid ||
          (isMedicalAid && useFlatRate && tariffs?.length === 0)) &&
        tariffs?.length > 0
      ) {
        newServiceLines = tariffs.map((tariff, i) => {
          const rawRate = String(tariff.rate || "").replace(/[^0-9.]/g, "");
          const rate = parseFloat(rawRate) || 0;

          const baseLine = withTemplate({
            ...baseLineInfoCommon,
            lineNumber: index + i + 1,
            tariffCode: tariff.code || "",
            description: tariff.description || fallbackDescription,
            quantity: tariff.qty || 1,
            rate: rate.toFixed(2),
          });

          return applyModifiersToLine(baseLine, tariff.modifiers || []);
        });
      }

      // ---------------------------
      // CASE 2: Flat-rate only (no tariffs or private flow)
      // ---------------------------
      else if (
        !isMedicalAid ||
        (isMedicalAid && useFlatRate && tariffs?.length === 0)
      ) {
        const baseLine = withTemplate({
          ...baseLineInfoCommon,
          tariffCode: "",
          description: fallbackDescription,
          quantity: 1,
          rate: fallbackFlatRate.toFixed(2),
        });

        newServiceLines.push(applyModifiersToLine(baseLine));
      }

      // ---------------------------
      // CASE 3: Medical aid + useFlatRate + tariffs present (your existing logic)
      // Here you fetch MSR quantity, but rate is still tariff.rate
      // ✅ Year now derived from the *actual line* after template.
      // ---------------------------
      else if (isMedicalAid && useFlatRate && tariffs?.length > 0) {
        newServiceLines = await Promise.all(
          tariffs.map(async (tariff, i) => {
            const baseLinePre = withTemplate({
              ...baseLineInfoCommon,
              lineNumber: index + i + 1,
              tariffCode: tariff.code || "",
              description: tariff.description || fallbackDescription,
              quantity: tariff.qty || 1, // temp, overwritten by MSR quantity
              rate: (
                parseFloat(String(tariff.rate || "").replace(/[^0-9.]/g, "")) ||
                0
              ).toFixed(2),
            });

            const lineDate = getLineServiceDate(
              baseLinePre,
              fallbackInvoiceDate
            );
            const msrYear = getYearFromYYYYMMDD(lineDate);

            const msr = await getMSRRate(
              selectedPractice.practiceNumber,
              tariff.code,
              account.accountMedicalAidOptionCode,
              msrYear
            );

            const baseLine = {
              ...baseLinePre,
              quantity: msr.quantity,
            };

            return applyModifiersToLine(baseLine, tariff.modifiers || []);
          })
        );
      }

      // ---------------------------
      // CASE 4: Medical aid, and MSR rate is used when useFlatAndMedicalAidRate
      // ✅ Year now derived from the actual line after template.
      // ---------------------------
      else {
        newServiceLines = await Promise.all(
          tariffs.map(async (tariff, i) => {
            let quantity = tariff.qty || 1;
            let invrate = 0;
            const description = tariff.description || fallbackDescription;

            // Build the line FIRST (with template) so year is correct
            const baseLinePre = withTemplate({
              ...baseLineInfoCommon,
              lineNumber: index + i + 1,
              tariffCode: tariff.code || "",
              description,
              quantity,
              rate: "0.00", // set below
            });

            const lineDate = getLineServiceDate(
              baseLinePre,
              fallbackInvoiceDate
            );
            const msrYear = getYearFromYYYYMMDD(lineDate);

            if (useFlatAndMedicalAidRate) {
              const msr = await getMSRRate(
                selectedPractice.practiceNumber,
                tariff.code,
                account.accountMedicalAidOptionCode,
                msrYear
              );
              invrate = Number(msr.rate || 0);
              quantity = msr.quantity;
            } else if (useFlatRate) {
              const rawRate = String(tariff.rate || "").replace(/[^0-9.]/g, "");
              invrate = parseFloat(rawRate) || 0;
            }

            const baseLine = {
              ...baseLinePre,
              quantity,
              originalRate: Number(invrate || 0).toFixed(2), // ✅ base before modifiers
              rate: Number(invrate || 0).toFixed(2),
            };

            const enriched = enrichModifiers(
              tariff.modifiers || [],
              modifierCatalog
            );

            return applyModifiersToLine(baseLine, enriched);
          })
        );
      }

      // ---------------------------
      // NAPPI lines (no modifiers)
      // ---------------------------
      const nappis = selectedService.pricing?.selectedNappi || [];
      if (nappis.length) {
        const nappiLines = nappis.map((n, i) =>
          withTemplate({
            ...baseLineInfoCommon,
            type: "nappi",
            lineNumber: index + newServiceLines.length + i + 1,
            selectedNappi: n,
            nappiCode: n.nappiCode || "",
            description: n.name || fallbackDescription,
            quantity: 1,
            rate:
              typeof n.rate === "number"
                ? n.rate.toFixed(2)
                : String(n.rate ?? "0.00"),
          })
        );

        newServiceLines = [...newServiceLines, ...nappiLines];
      }

      // Replace current line with the new group
      const updatedLines = [...lines];
      updatedLines.splice(index, 1, ...newServiceLines);
      setLines(updatedLines);

      // Set default template if we didn't have one yet
      if (!defaultAppointmentTemplate && newServiceLines[0]) {
        setDefaultAppointmentTemplate(
          createDefaultTemplate(newServiceLines[0])
        );
      }
    },
    [
      services,
      lines,
      account?.accountMedicalAid,
      account?.accountMedicalAidOptionCode,
      selectedPractice?.practiceNumber,
      selectedPatientId,
      defaultAppointmentTemplate,
      createDefaultTemplate,
      getMSRRate,
      primaryIcds,
      secondaryIcds,
      getIcdCode,
      invoiceDate,
    ]
  );

  const handleTariffLineSelect = useCallback(
    async (index, tariff) => {
      if (!selectedPractice?.practiceNumber) return;

      const tariffCode = tariff.code;

      const fallbackInvoiceDate =
        invoiceDate || new Date().toISOString().slice(0, 10);

      // Derive year from THIS line’s appointment date (after any edits)
      const currentLine = lines[index] || {};
      const lineDate = getLineServiceDate(currentLine, fallbackInvoiceDate);
      const msrYear = getYearFromYYYYMMDD(lineDate);

      const msr = await getMSRRate(
        selectedPractice.practiceNumber,
        tariff.code,
        account.accountMedicalAidOptionCode,
        msrYear
      );

      setLines((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          selectedTariff: tariff,
          tariffCode,
          description: `${tariff.description} (${tariff.typeCode})`,
          quantity: msr.quantity,
          rate: (msr.rate ?? 0).toFixed(2),
        };
        return updated;
      });
    },
    [
      getMSRRate,
      selectedPractice?.practiceNumber,
      account.accountMedicalAidOptionCode,
      lines,
      invoiceDate,
    ]
  );

  const handleNappiLineSelect = useCallback((index, nappi) => {
    setLines((prev) => {
      const updated = [...prev];
      const rateRaw = nappi.rate ?? "0.00";
      const rate =
        typeof rateRaw === "number" ? rateRaw.toFixed(2) : String(rateRaw);
      updated[index] = {
        ...updated[index],
        selectedNappi: nappi,
        nappiCode: nappi.nappiCode || nappi.code || "",
        description: nappi.name || "",
        quantity: updated[index].quantity || 1,
        rate,
      };
      return updated;
    });
  }, []);

  const handleLineChange = useCallback(
    async (index, field, value) => {
      // Handle special async case
      if (field === "patientId") {
        const patientId = value;
        const selectedPatient = patients.find((p) => p._id === patientId);
        const appointments = await ensureAppointmentsForPatient(patientId);
        const firstAppointmentId = appointments[0]?._id || "";

        setLines((prev) => {
          const updated = [...prev];
          const currentLine = updated[index] || {};
          updated[index] = {
            ...currentLine,
            appointmentId: firstAppointmentId,
            patientFullNames: selectedPatient?.patientFirstName || "",
            patientLastName: selectedPatient?.patientLastName || "",
          };
          return updated;
        });
        return;
      }

      // General field update
      setLines((prev) => {
        const updated = [...prev];
        const currentLine = updated[index] || {};
        let newLine = { ...currentLine, [field]: value };

        // If primary ICD changed, recompute oti
        if (field === "icd101") {
          const matchedICD = selectedICD10s.find(
            (icd) => getIcdCode(icd.item) === value
          );
          newLine.oti = matchedICD?.isPMB ? "PB" : "A";
        }

        updated[index] = newLine;
        console.log(`Line ${index} updated field ${field} to`, value);
        console.log("Updated line:", newLine);
        return updated;
      });
    },
    [patients, ensureAppointmentsForPatient, selectedICD10s]
  );

  const handleRemoveLine = useCallback((removeIndex) => {
    setLines((prevLines) =>
      prevLines
        .filter((_, i) => i !== removeIndex)
        .map((line, i) => ({ ...line, lineNumber: i + 1 }))
    );
  }, []);

  const getStatusVariant = useCallback((status) => {
    switch ((status || "").toLowerCase()) {
      case "draft":
        return "warning";
      case "pending":
        return "secondary";
      case "posted":
        return "success";
      case "paid":
        return "primary";
      case "claim in progress":
      case "posted - claim in progress":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "dark";
    }
  }, []);

  // ======= Invoice construction & persistence =======
  const constructInvoice = useCallback(
    (overrideStatus) => {
      const patientObj = patients.find((p) => p._id === selectedPatientId);
      const selectedPract = practitioners.find(
        (p) => p.id === selectedPractitionerId
      );

      // Prefer ICDs from the invoice selections; if none, derive from attached case
      const icd10CodesForInvoice =
        icdSelections?.length > 0
          ? icdSelections.map((s) => ({
              ...s.item,
              isPrimary: !!s.isPrimary,
              useOnInvoice: !!s.useOnInvoice,
              isPMB: !!s.isPMB,
              pmbAuthNo: s.pmbAuthNo || "",
              pmbStartDate: s.pmbStartDate || "",
              pmbEndDate: s.pmbEndDate || "",
            }))
          : selectedICD10s?.length > 0
          ? selectedICD10s.map((s) => ({
              ...s.item,
              isPrimary: !!s.isPrimary,
              useOnInvoice: !!s.useOnInvoice,
              isPMB: !!s.isPMB,
              pmbAuthNo: s.pmbAuthNo || "",
              pmbStartDate: s.pmbStartDate || "",
              pmbEndDate: s.pmbEndDate || "",
            }))
          : attachedCase
          ? [
              ...(attachedCase.icd101
                ? [{ ICD10_Code: attachedCase.icd101, isPrimary: true }]
                : []),
              ...(attachedCase.icd102 || []).map((code) => ({
                ICD10_Code: code,
                isPrimary: false,
              })),
            ]
          : [];
      console.log("ICD10 Codes for Invoice:", icd10CodesForInvoice);
      // Build a compatible inPatientCase block (legacy consumers) from attachedCase when present
      const inPatientCaseCompat = attachedCase
        ? {
            caseAuthNumber: attachedCase.authorizationNumber || "",
            caseBHF: attachedCase.referringDoctorBHF || "",
            caseHPCSA: attachedCase.referringDoctorHPCSA || "",
            caseICD10: attachedCase.icd101 || "",
          }
        : null;

      // Optional rich case block to persist full case details with the invoice
      const caseBlock = attachedCase
        ? {
            id: attachedCase._id || null,
            description: attachedCase.caseDescription || "",
            referringDoctorName: attachedCase.referringDoctorName || "",
            referringDoctorBHF: attachedCase.referringDoctorBHF || "",
            referringDoctorHPCSA: attachedCase.referringDoctorHPCSA || "",
            authorizedBy: attachedCase.authorizedBy || "",
            authorizationNumber: attachedCase.authorizationNumber || "",
            startDate: attachedCase.caseStartDate || "", // MM-DD-YYYY
            endDate: attachedCase.caseEndDate || "", // MM-DD-YYYY
            icd101: attachedCase.icd101 || "",
            icd102: Array.isArray(attachedCase.icd102)
              ? attachedCase.icd102
              : [],
          }
        : null;

      const newInvoice = {
        accountId: account._id,
        accountName: `${account.accountFullNames} ${account.accountLastName}`,
        accountEmail: account.accountEmail,
        medicalAid: {
          name: account.accountMedicalAid,
          plan: account.accountMedicalAidPlan,
          option: account.accountMedicalAidOption,
          number: account.accountMedicalAidNumber,
          optionCode: account.accountMedicalAidOptionCode,
        },
        practice: selectedPractice
          ? {
              practiceNumber: selectedPractice.practiceNumber,
              practiceName: selectedPractice.practiceName,
              vatNumber: selectedPractice.vatNumber,
            }
          : null,
        practitioner: selectedPract
          ? {
              id: selectedPractitionerId,
              firstName: selectedPract.firstName,
              lastName: selectedPract.lastName,
              hpcsa: selectedPract.hpcsa,
            }
          : null,
        invoiceNumber: invoiceNumber || null,
        invoiceDate,
        totalAmount,
        totalMedicalAidAmount,
        medAidTotal: totalMedicalAidAmount,
        totalPatientAmount,
        patientTotal: totalPatientAmount,
        placeOfService: selectedPlaceOfService,
        outPatient: outpatient,

        // NEW: persist selected case cleanly
        caseId: attachedCase ? attachedCase._id || null : null,
        case: caseBlock,

        // Keep legacy block (mapped from selected case when present)
        inPatientCase: inPatientCaseCompat,

        // ICDs for the invoice header
        icd10Codes: icd10CodesForInvoice,

        patient: {
          patientId: patientObj?._id,
          patientFirstName: patientObj?.patientFirstName,
          patientLastName: patientObj?.patientLastName,
          patientMedicalAid: patientObj?.patientMedicalAid,
          patientMedicalAidNumber: patientObj?.patientMedicalAidNumber,
          patientMedicalAidOptionCode: patientObj?.patientMedicalAidOptionCode,
          patientMedicalAidPlanCode: patientObj?.patientMedicalAidPlanCode,
          patientMedicalAidDependantCode:
            patientObj?.patientMedicalAidDependantCode,
        },

        outstandingBalance: {
          patientOutstanding: totalPatientAmount,
          medAidOutstanding: totalMedicalAidAmount,
          totalOutstanding: totalAmount,
          paidAmount: 0,
        },

        lines: lines?.map((line) => {
          const full =
            parseFloat(String(line.rate || "0").replace(/[, ]+/g, "")) || 0;
          let patientAmount = 0;
          let medAidAmount = 0;
          const hasBillableCode = Boolean(line.tariffCode || line.nappiCode);

          if (
            !account.accountMedicalAid ||
            line.privatePayment ||
            !hasBillableCode
          ) {
            patientAmount = full;
          } else {
            medAidAmount = full;
          }

          // Recompute oti per line, based on that line’s icd101
          const matchedICD = icd10CodesForInvoice.find(
            (icd) => icd.ICD10_Code === line.icd101 && icd.isPMB
          );
          const lineOti = matchedICD ? "PB" : "A";

          return {
            lineNumber: line.lineNumber,
            type: line.type,
            serviceId: line.serviceId || null,
            selectedService: line.selectedService || null,
            selectedTariff: line.selectedTariff || null,
            selectedNappi: line.selectedNappi || null,
            oti: lineOti,
            icd101: line.icd101,
            icd102:
              Array.isArray(line.icd10Secondary) && line.icd10Secondary.length
                ? line.icd10Secondary.join(";")
                : line.icd102 || "",
            icd10Secondary: Array.isArray(line.icd10Secondary)
              ? line.icd10Secondary
              : [],
            tariffCode: line.tariffCode,
            nappiCode: line.nappiCode || null,
            modifier: line.modifier || null,
            originalRate: line.originalRate || null,
            description: line.description,
            quantity: line.quantity,
            rate: full,
            privatePayment: line.privatePayment || false,
            patientAmount,
            medAidAmount,
            appointmentDateSet: line.useCustomDate
              ? null
              : line.appointmentStartSet
              ? line.appointmentStartSet.slice(0, 10)
              : null,
            appointmentStartSet: line.useCustomDate
              ? null
              : line.appointmentStartSet || null,
            appointmentEndSet: line.useCustomDate
              ? null
              : line.appointmentEndSet || null,
            appointmentDateCustom: line.useCustomDate
              ? line.appointmentCustomStart
                ? line.appointmentCustomStart.slice(0, 10)
                : line.appointmentDate || null
              : null,
            appointmentCustomStart: line.useCustomDate
              ? line.appointmentCustomStart || null
              : null,
            appointmentCustomEnd: line.useCustomDate
              ? line.appointmentCustomEnd || null
              : null,
            appointmentId: line.appointmentId || null,
          };
        }),
      };

      if (overrideStatus) newInvoice.status = overrideStatus;

      setInvoice(newInvoice);
      console.log("New invoice created:", newInvoice);
      return newInvoice;
    },
    [
      // deps
      account._id,
      account.accountFullNames,
      account.accountLastName,
      account.accountEmail,
      account.accountMedicalAid,
      account.accountMedicalAidPlan,
      account.accountMedicalAidOption,
      account.accountMedicalAidNumber,
      account.accountMedicalAidOptionCode,
      icdSelections,
      attachedCase,
      invoiceNumber,
      invoiceDate,
      totalAmount,
      totalMedicalAidAmount,
      totalPatientAmount,
      selectedPlaceOfService,
      outpatient,
      lines,
      patients,
      practitioners,
      selectedPatientId,
      selectedPractitionerId,
      selectedPractice,
    ]
  );

  const refreshCases = useCallback(async (pid) => {
    if (!pid) return;
    const arr = await getCasesByPatientId(pid);
    setCases(Array.isArray(arr) ? arr : []);
  }, []);

  // call once on mount / patient change
  useEffect(() => {
    refreshCases(selectedPatientId);
  }, [selectedPatientId, refreshCases]);

  const saveNewInvoice = useCallback(
    async (onStart, onEnd) => {
      if (!validateInvoiceFields() || !validateInvoiceLines()) return;
      onStart?.("saveNew");
      try {
        const invoiceToSave = constructInvoice("Pending");
        const response = await createInvoice(invoiceToSave);
        const newNumber =
          response.invoiceNumber ?? response.invoice?.invoiceNumber;
        const newStatus = response.status ?? response.invoice?.status;

        setInvoiceNumber(newNumber);
        setInvoiceStatus(newStatus || "Pending");

        setSuccessMessage(true);
        window.scrollTo({ top: 0, behavior: "smooth" });

        setTimeout(() => {
          setSuccessMessage(false);
          if (window.innerWidth <= 933) {
            navigate("/bookings");
          } else {
            navigate(`/accounts/${account._id}`);
          }
        }, 3000);
      } finally {
        onEnd?.();
      }
    },
    [
      validateInvoiceFields,
      validateInvoiceLines,
      constructInvoice,
      navigate,
      account._id,
    ]
  );

  const saveAndPostNewInvoice = useCallback(
    async (onStart, onEnd) => {
      if (!validateInvoiceFields() || !validateInvoiceLines()) return;
      onStart?.("savePostNew");
      try {
        const invoiceToSave = constructInvoice();
        const hasMedicalAid =
          !!invoiceToSave.medicalAid?.name &&
          invoiceToSave.lines.some((line) => !line.privatePayment);

        invoiceToSave.status = hasMedicalAid
          ? "Posted - Claim in Progress"
          : "Posted";
        const response = await createInvoice(invoiceToSave);
        const newNumber =
          response.invoiceNumber ?? response.invoice?.invoiceNumber;
        const newStatus = response.status ?? response.invoice?.status;
        const responseId = response.invoiceId || response.invoice?._id;

        setInvoiceNumber(newNumber);
        setInvoiceStatus(newStatus || "Posted");

        setSuccessMessage(true);
        window.scrollTo({ top: 0, behavior: "smooth" });

        setTimeout(() => {
          setSuccessMessage(false);
          if (window.innerWidth <= 933) {
            navigate("/bookings");
          } else {
            navigate(`/invoices/posted/${responseId}`);
          }
        }, 3000);
      } finally {
        onEnd?.();
      }
    },
    [validateInvoiceFields, validateInvoiceLines, constructInvoice, navigate]
  );

  const updateExistingInvoiceAndClaim = useCallback(
    async (onStart, onEnd) => {
      console.log("validateInvoiceFields:", validateInvoiceFields());
      console.log("validateInvoiceLines:", validateInvoiceLines());
      if (!validateInvoiceFields() || !validateInvoiceLines()) return;
      onStart?.("update");
      try {
        const invoiceToSave = constructInvoice();
        const claim = await getClaimByInvoiceId(invoice._id);
        console.log("🔍 Fetched claim for invoice:", claim);
        const hasMedicalAid =
          !!invoiceToSave.medicalAid?.name &&
          invoiceToSave.lines.some((line) => !line.privatePayment);

        const updatedInvoice = {
          ...invoice,
          status:
            hasMedicalAid && feedback?._id && invoice.status !== "Pending"
              ? "Posted - Claim In Progress"
              : hasMedicalAid && invoice.status === "Posted"
              ? "Posted"
              : hasMedicalAid &&
                feedback?._id &&
                invoice.status === "Posted - Claim In Progress"
              ? "Posted - Claim In Progress"
              : invoice.status,
        };

        setInvoice(updatedInvoice);

        if (hasMedicalAid && claim?._id) {
          const updatedClaim = {
            ...claim,
            status: "Claim Updated",
            lineResults: [],
          };

          await updateClaim(claim._id, updatedClaim);
        }

        await updateInvoice(invoice._id, {
          ...invoiceToSave,
          status: updatedInvoice.status,
        });

        setSuccessMessage(true);
        window.scrollTo({ top: 0, behavior: "smooth" });

        setTimeout(() => {
          setSuccessMessage(false);
          if (invoice.status === "Pending") {
            navigate(`/accounts/${account._id}`);
          } else {
            navigate(`/invoices/posted/${invoice._id}`);
          }
        }, 3000);
      } finally {
        onEnd?.();
      }
    },
    [
      validateInvoiceFields,
      validateInvoiceLines,
      constructInvoice,
      invoice,
      feedback,
      navigate,
      account._id,
    ]
  );

  const handlePost = useCallback(
    async (onStart, onEnd) => {
      onStart?.("post");
      try {
        const invoiceToSave = constructInvoice();

        const hasMedicalAid =
          !!invoiceToSave.medicalAid?.name &&
          invoiceToSave.lines.some((line) => !line.privatePayment);

        const updatedStatus = hasMedicalAid
          ? "Posted - Claim in Progress"
          : "Posted";

        const updatedInvoice = {
          ...invoiceToSave,
          status: updatedStatus,
        };

        const response = await updateInvoice(invoice._id, updatedInvoice);

        setInvoiceStatus(updatedStatus);

        if (typeof onInvoiceUpdated === "function") {
          onInvoiceUpdated();
        }

        if (response) {
          setSuccessMessage(true);
          window.scrollTo({ top: 0, behavior: "smooth" });
          setTimeout(() => {
            setSuccessMessage(false);
            navigate(`/invoices/posted/${invoice._id}`);
          }, 3000);
        }
      } catch (error) {
        console.error("❌ Error posting invoice:", error);
      } finally {
        onEnd?.();
      }
    },
    [constructInvoice, invoice?._id, onInvoiceUpdated, navigate]
  );

  const selectedPract = useMemo(
    () => practitioners.find((p) => p.id === selectedPractitionerId),
    [practitioners, selectedPractitionerId]
  );

  // Use whatever field your user object actually has:

  // ======= Effects =======
  // Handle nav state incoming invoice
  useEffect(() => {
    if (!practice?.length) return;
    // Handle navigation from appointment screen
    if (location.state?.appointmentId) {
      const {
        patientId,
        practitionerId,
        practiceId,
        appointmentId,
        appointmentStart,
        appointmentEnd,
      } = location.state;

      // Set patient & practitioner
      if (patientId) setSelectedPatientId(patientId);
      if (practitionerId) setSelectedPractitionerId(practitionerId);
      // Match practice
      if (practiceId) {
        const matchedPractice = practice.find(
          (p) => p.practiceNumber === practiceId
        );
        if (matchedPractice) {
          setSelectedPracticeId(practiceId);
          console.log("practiceId from nav state:", practiceId);
          setSelectedPractice(matchedPractice);
          console.log("Matched practice from nav state:", matchedPractice);
        }
      }

      // Only update lines after they exist
      if (lines.length > 0) {
        const updatedLines = lines.map((line) => ({
          ...line,
          useCustomDate: false,
          appointmentId,
          appointmentStartSet: appointmentStart || null,
          appointmentEndSet: appointmentEnd || null,
          appointmentDateSet: appointmentStart
            ? appointmentStart.slice(0, 10)
            : null,
          appointmentDate: appointmentStart
            ? appointmentStart.slice(0, 10)
            : null,
          appointmentCustomStart: null,
          appointmentCustomEnd: null,
          appointmentDateCustom: null,
        }));

        setLines(updatedLines);
        setLineApptInitial({
          useCustomDate: false,
          appointmentId,
          appointmentStartSet: appointmentStart || null,
          appointmentEndSet: appointmentEnd || null,
          appointmentDateSet: appointmentStart
            ? appointmentStart.slice(0, 10)
            : null,
          appointmentDate: appointmentStart
            ? appointmentStart.slice(0, 10)
            : null,
          appointmentCustomStart: null,
          appointmentCustomEnd: null,
          appointmentDateCustom: null,
        });
      }

      return;
    }
  }, [location.state, lines.length, practice, initialInvoiceApp]);

  const disciplineCode =
    selectedPract?.disciplineCode || // <-- if your practitioner has this
    selectedPract?.practitionerTypeCode || // <-- or this
    selectedPractice?.practiceDisciplineCode ||
    initialDisciplineCode || // fallback
    null;

  // Update icd primaries on change
  useEffect(() => {
    setLines((prev) =>
      prev.map((l) => {
        if (!l.icd101) return l;
        const stillPrimary = primaryIcds.some(
          (icd) => getIcdCode(icd) === l.icd101
        );
        if (stillPrimary || primaryIcds.length === 0) return l;
        return { ...l, icd101: getIcdCode(primaryIcds[0]) };
      })
    );
  }, [primaryIcds, getIcdCode]);

  // Load patients + services
  useEffect(() => {
    async function loadPatientsAndServices() {
      const patientData = await getAccountPatients(account._id);
      setPatients(patientData);

      const servicesData = await getServicesByPracticeNumbers(
        account.accountPractice
      );
      setServices(servicesData);
      console.log("Loaded services for account practices:", servicesData);
    }
    loadPatientsAndServices();
  }, [account]);

  useEffect(() => {
    if (!selectedPatientId) return;
    if (icdSelections.length > 0) return; // ICDs already selected manually or from loaded invoice

    async function fetchInvoiceICDs() {
      const data = await getPatientInvoiceICDs(selectedPatientId);

      if (!Array.isArray(data) || data.length === 0) return;

      setSelectedICD10s(data);
      setIcdSelections(
        data.map((s) => ({
          item: s.item ?? s,
          isPrimary: !!s.isPrimary,
          isPMB: !!s.isPMB,
          pmbAuthNo: s.pmbAuthNo || "",
          pmbStartDate: s.pmbStartDate || "",
          pmbEndDate: s.pmbEndDate || "",
          useOnInvoice: true,
        }))
      );

      // Update lines: inject first primary ICD10
      const firstPrimary = data.find((s) => s.isPrimary);
      if (firstPrimary?.item) {
        const code =
          firstPrimary.item.ICD10_Code || firstPrimary.item.ICD10_3_Code || "";
        setLines((prev) =>
          prev.map((line) => (!line.icd101 ? { ...line, icd101: code } : line))
        );
      }
    }
    console.log("ICD Selections:", icdSelections);
    fetchInvoiceICDs();
  }, [selectedPatientId, icdSelections]);

  // Defaults for practice, practitioner, POS
  useEffect(() => {
    if (practice?.length && !selectedPracticeId) {
      const firstPractice = practice[0];
      setSelectedPracticeId(firstPractice.practiceNumber);
      setSelectedPractice(firstPractice); // ✅ Also set the object
      console.log(
        "Defaulting to first practice:",
        firstPractice.practiceNumber
      );
    }
  }, [practice]);

  useEffect(() => {
    const disc = disciplineCode;
    if (!disc) {
      setModifierCatalog(new Map());
      return;
    }

    (async () => {
      try {
        const mods = await getModifierByDisciplineCode(disc);
        const map = new Map();
        (mods || []).forEach((m) => {
          if (!m?.code) return;
          map.set(String(m.code), {
            adjustment: m.adjustment,
            adjustmentPercentage: m.adjustmentPercentage,
            description: m.description,
          });
        });
        setModifierCatalog(map);
      } catch (e) {
        console.error("Failed to load modifier catalog:", e);
        setModifierCatalog(new Map());
      }
    })();
  }, [disciplineCode]);

  // Only pick first POS if none set
  useEffect(() => {
    if (placesOfService?.length && !selectedPlaceOfService) {
      const first = placesOfService[0];
      setSelectedPlaceOfService(first.HCFA_code);
      setOutpatient(first.out ? "O" : "I");
    }
  }, [placesOfService, selectedPlaceOfService]);

  // Load practitioners on practice change
  useEffect(() => {
    if (!selectedPracticeId) return;

    async function getPractitioners() {
      const data = await fetchallUsersByPractices([selectedPracticeId]);
      console.log("Fetched users for practice:", data);
      // ✅ Only keep users whose role is exactly "Practitioner"
      const practitionersOnly = (data || []).filter((u) => {
        if (Array.isArray(u.role)) {
          return u.role.includes("Practitioner");
        }
        return u.role === "Practitioner";
      });

      setPractioners(practitionersOnly);

      // ✅ Set default only if it's a Practitioner
      if (!selectedPractitionerId && practitionersOnly.length > 0) {
        setSelectedPractitionerId(practitionersOnly[0].id);
      }

      console.log("Loaded practitioners for practice:", practitionersOnly);
    }

    getPractitioners();
  }, [selectedPracticeId]);

  // Separate effect: Set initial practitioner (only runs once on mount or when initial data loads)
  useEffect(() => {
    // Skip if we already have a practitioner selected
    if (selectedPractitionerId) return;

    // Skip if we don't have practitioners loaded yet
    if (!practitioners.length) return;

    const desiredId =
      initialInvoice?.practitioner?.id || initialInvoiceApp?.practitioner?.id;
    console.log("Setting initial practitioner ID:", desiredId);

    if (desiredId && practitioners.some((p) => p.id === desiredId)) {
      setSelectedPractitionerId(desiredId);
    } else {
      // Fall back to first practitioner
      setSelectedPractitionerId(practitioners[0].id);
    }
  }, [
    practitioners,
    initialInvoice?.practitioner?.id,
    initialInvoiceApp?.practitioner?.id,
    selectedPractitionerId,
  ]);

  // Apply initialInvoice structure and fetch appts for that patient (once)
  useEffect(() => {
    if (initialInvoiceApp || !id) return;
    let isMounted = true;
    (async () => {
      try {
        const loaded = await getInvoiceById(id);
        console.log("🚚 Loaded invoice by ID:", loaded);
        if (!isMounted || !loaded) return;

        // Basic invoice fields
        setInvoice(loaded);
        setInvoiceDate(
          loaded.invoiceDate || new Date().toISOString().split("T")[0]
        );
        setInvoiceNumber(loaded.invoiceNumber || "");
        setInvoiceStatus(loaded.status || "Draft");
        setSelectedPracticeId(loaded.practice?.practiceNumber || "");
        console.log(
          "Setting selected practice ID from loaded invoice:",
          loaded.practice?.practiceNumber
        );
        setInitialDisciplineCode(loaded.practice?.practiceDisciplineCode || "");
        console.log(
          "Setting initial discipline code from loaded invoice:",
          loaded.practice?.practiceDisciplineCode
        );
        setSelectedPractice(loaded.practice || null);
        setSelectedPatientId(loaded.patient?.patientId || "");
        setSelectedPractitionerId(loaded.practitioner?.id || "");
        setSelectedPlaceOfService(loaded.placeOfService || "");
        setInPatientCase(
          loaded.inPatientCase || {
            caseAuthNumber: "",
            caseBHF: "",
            caseHPCSA: "",
            caseICD10: "",
          }
        );

        // ✅ Handle both appointmentDate and appointmentDateCustom at root level
        if (loaded.appointmentDateCustom) {
          setAppointmentDate(loaded.appointmentDateCustom);
        } else if (loaded.appointmentDate) {
          setAppointmentDate(loaded.appointmentDate);
        } else {
          setAppointmentDate("");
        }

        setOutpatient(loaded.outPatient !== "I");
        setLines(loaded.lines || []);
        setSelectedICD10s(loaded.icd10Codes || []);

        // ✅ Respect isPrimary from Mongo
        if (Array.isArray(loaded.icd10Codes)) {
          const seeded = loaded.icd10Codes.map((x) => ({
            item: x,
            isPrimary: !!x.isPrimary,
            isPMB: !!x.isPMB,
            pmbAuthNo: x.pmbAuthNo || "",
            pmbStartDate: x.pmbStartDate || "",
            pmbEndDate: x.pmbEndDate || "",
          }));
          setIcdSelections(seeded);
          console.log("ICD selections from loaded invoice:", seeded);
        }

        // ✅ Update each line’s appointment/custom date fields
        if (loaded.lines?.length > 0) {
          const transformedLines = loaded.lines.map((line) => {
            const useCustomDate = !!line.appointmentDateCustom;
            return {
              ...line,
              patientId: line.patientId || loaded.patient?.patientId || "", // ✅ ensure every line has it
              useCustomDate,
              appointmentDate: useCustomDate
                ? line.appointmentDateCustom || ""
                : line.appointmentDateSet || "",
              appointmentStartSet: line.appointmentStartSet || null,
              appointmentEndSet: line.appointmentEndSet || null,
              appointmentCustomStart: line.appointmentCustomStart || null,
              appointmentCustomEnd: line.appointmentCustomEnd || null,
              appointmentDateCustom: line.appointmentDateCustom || null,
            };
          });

          setLines(transformedLines);
          setDefaultAppointmentTemplate(
            createDefaultTemplate(transformedLines[0])
          );
          setLineApptInitial(transformedLines[0]);
        }

        setAttachedCase(loaded.case || null);
      } catch (e) {
        console.error("Failed to load invoice by id:", e);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [initialInvoiceApp, id, createDefaultTemplate]);

  // Preload first patient appointments (cached)
  useEffect(() => {
    async function preloadFirstPatientAppointments() {
      if (patients?.length && !lineAppointments[patients[0]._id]) {
        const data = await getAppointmentsByPatientId(patients[0]._id);
        setLineAppointments((prev) => ({
          ...prev,
          [patients[0]._id]: data,
        }));
      }
    }
    preloadFirstPatientAppointments();
  }, [patients, lineAppointments]);

  // Populate default service line coming from navigation
  useEffect(() => {
    const populateDefaultServiceLine = async () => {
      if (incomingServiceId && services.length > 0 && lines.length === 0) {
        await handleServiceSelect(0, incomingServiceId);

        // After service is added, apply appointment data to ALL lines if available
        if (location.state?.appointmentId) {
          const { appointmentId, appointmentStart, appointmentEnd } =
            location.state;

          setLines((prev) =>
            prev.map((line) => ({
              ...line,
              useCustomDate: false,
              appointmentId,
              appointmentStartSet: appointmentStart || null,
              appointmentEndSet: appointmentEnd || null,
              appointmentDateSet: appointmentStart
                ? appointmentStart.slice(0, 10)
                : null,
              appointmentDate: appointmentStart
                ? appointmentStart.slice(0, 10)
                : null,
            }))
          );
        }
      }
    };
    populateDefaultServiceLine();
  }, [
    incomingServiceId,
    services,
    lines.length,
    handleServiceSelect,
    location.state,
  ]);

  // ======= Render =======

  const renderPracticeSection = () => (
    <>
      <Form.Group className="mb-3">
        <Form.Label className="fw-bold">Select Practice</Form.Label>
        <Form.Select
          className="w-75 ms-1"
          value={selectedPracticeId || ""}
          onChange={(e) => {
            const selected = practice.find(
              (p) => p.practiceNumber === e.target.value
            );
            setSelectedPracticeId(e.target.value);
            console.log("Selected practice ID:", e.target.value);
            console.log("Selected practice object:", selected);
            setSelectedPractice(selected);
          }}
          isInvalid={!!invoiceErrors.practice} // ✅ Added this
        >
          <option value="">Select a practice...</option>
          {practice?.map((p) => (
            <option key={p.practiceNumber} value={p.practiceNumber}>
              {p.practiceName}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">
          {invoiceErrors.practice}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="fw-bold">Select Practitioner</Form.Label>
        <Form.Select
          className="w-75 ms-1"
          value={selectedPractitionerId || ""}
          onChange={(e) => setSelectedPractitionerId(e.target.value || "")}
          isInvalid={!!invoiceErrors.practitioner}
        >
          <option value="">Select a practitioner...</option>
          {practitioners
            .filter((p) => p.role?.includes("Practitioner"))
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.firstName} {p.lastName}
              </option>
            ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">
          {invoiceErrors.practitioner}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-1">
        <Form.Label className="fw-bold">Place of Service</Form.Label>
        <Form.Select
          className="w-75 ms-1"
          value={selectedPlaceOfService}
          onChange={(e) => {
            const selectedCode = e.target.value;
            const selectedPOS = placesOfService.find(
              (pos) => pos.HCFA_code === selectedCode
            );
            setSelectedPlaceOfService(selectedCode);
            if (selectedPOS) {
              setOutpatient(selectedPOS.out ? "O" : "I");
            }
          }}
          isInvalid={!!invoiceErrors.placeOfService}
        >
          <option value="">Select a place of service...</option>
          {placesOfService.map((pos) => (
            <option key={pos.HCFA_code} value={pos.HCFA_code}>
              {pos.HCFA_code} - {pos.newDescription}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">
          {invoiceErrors.placeOfService}
        </Form.Control.Feedback>
      </Form.Group>
    </>
  );

  const pad = (n) => String(n).padStart(2, "0");
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return iso; // already a date-only string?
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  function deriveCaseView(c) {
    if (!c) return null;

    // Try icd from selections first; otherwise fall back to fields.
    let primaryICD = c.icd101 || null;
    let secondaryICDs = Array.isArray(c.icd102) ? c.icd102 : [];

    if (Array.isArray(c.icdSelections) && c.icdSelections.length) {
      const prim = c.icdSelections.find((s) => s.isPrimary);
      if (prim?.item?.ICD10_Code) primaryICD = prim.item.ICD10_Code;
      const secs = c.icdSelections
        .filter((s) => !s.isPrimary && s?.item?.ICD10_Code)
        .map((s) => s.item.ICD10_Code);
      if (secs.length) secondaryICDs = secs;
    }

    // Referrer could be flat or nested
    const refName =
      c.referringDoctorName || c.referrer?.name || c.referrerName || "—";
    const refBHF =
      c.referringDoctorBHF || c.referrer?.bhf || c.referrerBHF || "—";
    const refHPCSA =
      c.referringDoctorHPCSA || c.referrer?.hpcsa || c.referrerHPCSA || "—";

    return {
      title:
        (c.caseDescription && c.caseDescription.trim()) ||
        c.title ||
        c.caseName ||
        "Untitled Case",
      start: fmtDate(c.caseStartDate || c.startDate || c.start),
      end:
        c.caseEndDate || c.endDate || c.end
          ? fmtDate(c.caseEndDate || c.endDate || c.end)
          : "",
      authorizedBy: c.authorizedBy || c.authBy || "—",
      authorizationNumber: c.authorizationNumber || c.authNumber || "—",
      refName,
      refBHF,
      refHPCSA,
      primaryICD: primaryICD || "—",
      secondaryICDs: secondaryICDs,
    };
  }

  const renderCaseCard = () => {
    if (!attachedCase) return null;
    const v = deriveCaseView(attachedCase);

    return (
      <div className="border rounded mt-1">
        {/* Header — same look & feel as Modal.Header */}
        <div
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center justify-content-between px-3 py-2"
        >
          <h6 className="m-0">Attached Case</h6>
          <i
            className="fa-regular fa-pen-to-square"
            role="button"
            title="Edit case"
            onClick={() => setShowAddCase(true)}
          />
        </div>

        {/* Body — white background */}
        <div className="p-3" style={{ background: "#FFFFFF" }}>
          <Row className="mb-2">
            <Col xs={12} className="text-center">
              <div className="fw-semibold">{v.title}</div>
              <div className="text-muted small">
                {v.start}
                {v.end ? ` to ${v.end}` : ""}
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={6} md={6} className="text-end">
              <p className="mb-1">Referral</p>
              <p className="mb-1">Referral BHF</p>
              <p className="mb-1">Referral HPCSA</p>
              <p className="mb-1">Auth By</p>
              <p className="mb-1">Auth Number</p>
              <p className="mb-0">ICD-10</p>
            </Col>
            <Col xs={6} md={6} className="text-start">
              <p className="mb-1">{v.refName}</p>
              <p className="mb-1">{v.refBHF}</p>
              <p className="mb-1">{v.refHPCSA}</p>
              <p className="mb-1">{v.authorizedBy}</p>
              <p className="mb-1">{v.authorizationNumber}</p>
              <p className="mb-0">
                {v.primaryICD}
                {Array.isArray(v.secondaryICDs) && v.secondaryICDs.length
                  ? ` | ${v.secondaryICDs.join(", ")}`
                  : ""}
              </p>
            </Col>
          </Row>
        </div>
      </div>
    );
  };
  console.log("PatientId", {
    editingLineIndex,
    selectedPatientId,
    linePatientId: lines[editingLineIndex]?.patientId,
  });

  return (
    <>
      <header className="d-flex justify-content-between align-items-start mb-1">
        <div className="w-100 text-center">
          <h4 className="text-primary fw-normal">Invoice</h4>
        </div>
        <Row>
          <div className="text-end">
            <h6 className={`text-${getStatusVariant(invoiceStatus)} fw-bold`}>
              {invoiceStatus}
            </h6>
          </div>
        </Row>
      </header>

      {successMessage && (
        <Row className="mb-2">
          <Alert
            variant="success"
            className="w-100 d-flex align-items-center justify-content-between mt-3 fs-6 fw-normal"
          >
            <span>The information invoice updated successfully.</span>
          </Alert>
        </Row>
      )}

      <main>
        <Row>
          {/* Account Info */}
          <Col md={6} sm={12} xs={12}>
            <Row>
              <Col md={6}>
                <p className="ms-1 mb-1 fs-sm-4 fs-md-3 fw-bold">
                  {account.accountTitle} {account.accountFullNames}{" "}
                  {account.accountLastName}
                </p>
                <p className="ms-3 mb-0">{account.accountHomeAddress1}</p>
                <p className="ms-3 mb-1">
                  {account.accountHomeAddress2} {account.accountHomeAddressCity}
                  {account.accountHomeAddressPostalCode}
                </p>
                <p className="ms-3 mb-0">{account.accountTelNumberCell}</p>
                <p className="ms-3 mb-2">{account.accountEmail}</p>
              </Col>
              <Col md={6} sm={12} xs={12}>
                <div className="mt-3">
                  <p className="mb-0 fw-bold">Fund:</p>

                  {account.accountMedicalAid ? (
                    <>
                      <p className="ms-2 mb-0 d-flex align-items-center gap-2">
                        <span className="badge bg-primary">Medical Aid</span>
                        <span>{account.accountMedicalAid}</span>
                      </p>

                      {(account.accountMedicalAidPlan ||
                        account.accountMedicalAidOption) && (
                        <p className="ms-2 mb-0">
                          {account.accountMedicalAidPlan}
                          {account.accountMedicalAidPlan &&
                            account.accountMedicalAidOption &&
                            " | "}
                          {account.accountMedicalAidOption}
                        </p>
                      )}

                      <p className="ms-2 mb-0">
                        {account.accountMedicalAidNumber}
                      </p>
                    </>
                  ) : account.accountIODFund ? (
                    <>
                      <p className="ms-2 mb-0 d-flex align-items-center gap-2">
                        <span className="badge bg-warning text-dark">IOD</span>
                        <span>{account.accountIODFund}</span>
                      </p>

                      {(account.accountIODPlan || account.accountIODOption) && (
                        <p className="ms-2 mb-0">
                          {account.accountIODPlan}
                          {account.accountIODPlan &&
                            account.accountIODOption &&
                            " | "}
                          {account.accountIODOption}
                        </p>
                      )}

                      {account.accountIODClaimRefNumber && (
                        <p className="ms-2 mb-0">
                          <strong className="text-primary">Claim Ref:</strong> {account.accountIODClaimRefNumber}
                        </p>
                      )}

                      {account.accountIODEmployer && (
                        <p className="ms-2 mb-0">
                          <strong className="text-primary">Employer:</strong> {account.accountIODEmployer}
                        </p>
                      )}

                      {account.accountIODEmployerWCARegNum && (
                        <p className="ms-2 mb-0">
                          <strong className="text-primary">WCA Reg #:</strong> {account.accountIODEmployerWCARegNum}
                        </p>
                      )}
                      {account.accountIODDateOfInjury && (
                        <p className="ms-2 mb-0">
                          <strong className="text-primary">Date of Injury:</strong> {account.accountIODDateOfInjury}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="ms-2 mb-0">
                      <span className="badge bg-secondary">Private Patient</span>{" "}
                    </p>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6} sm={12} xs={12}>
                <Form.Group className="mt-3">
                  <Form.Label className="fw-bold">Select Patient</Form.Label>
                  <Form.Select
                    value={selectedPatientId || ""}
                    onChange={(e) => setSelectedPatientId(e.target.value)}
                    isInvalid={!!invoiceErrors.patient}
                  >
                    <option value="">Select a patient...</option>
                    {patients?.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.patientFirstName} {p.patientLastName}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {invoiceErrors.patient}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* ICD10 Search */}
              <Col md={5} sm={12} xs={12}>
                <Form.Group>
                  <Form.Label className="fw-bold mt-4"></Form.Label>
                  <div className="d-flex gap-2 mt-1">
                    <Button
                      variant="outline-primary"
                      className="rounded-0 w-50"
                      onClick={() => setShowICDModal(true)}
                    >
                      Select ICD-10(s)
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowAddCase(true)}
                      className="w-50 rounded-0"
                    >
                      Add Case
                    </Button>
                  </div>

                  <div className="mt-1">
                    {icdSelections.length === 0 ? (
                      <div className="text-muted">No ICD-10s selected.</div>
                    ) : (
                      <ul className="list-unstyled mb-0">
                        {icdSelections.map((s, idx) => {
                          const code = getIcdCode(s.item);
                          const line = getIcdOneLine(s.item);
                          return (
                            <li
                              key={code || idx}
                              className="d-flex align-items-center gap-2"
                            >
                              <span
                                className={`badge ${
                                  s.isPrimary ? "bg-primary" : "bg-secondary"
                                }`}
                              >
                                {s.isPrimary ? "Primary" : "Secondary"}
                              </span>
                              <span
                                className="text-truncate"
                                title={line}
                                style={{ maxWidth: 520 }}
                              >
                                {line}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {invoiceErrors.icd10 && (
                    <div className="text-danger mt-1">
                      {invoiceErrors.icd10}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Col>

          {/* Practice Info */}
          <Col md={6}>
            <Row>
              {/* LEFT half: show CASE if attached; otherwise show PRACTICE fields */}
              <Col md={6}>
                {attachedCase ? renderCaseCard() : renderPracticeSection()}
              </Col>

              {/* RIGHT half: invoice number/date always; if case attached, show PRACTICE under the date */}
              <Col md={6}>
                {invoiceNumber && (
                  <div>
                    <Form.Label className="fw-bold">Invoice Number:</Form.Label>
                    <span className="text-primary fw-bold ms-2 me-2">
                      {invoiceNumber}
                    </span>
                  </div>
                )}

                <Form.Group className="mb-2" controlId="invoiceDate">
                  <Form.Label className="fw-bold">Invoice Date</Form.Label>
                  <Form.Control
                    type="date"
                    className="w-50"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    readOnly={isPosted}
                    style={isPosted ? { backgroundColor: "#e9ecef" } : {}}
                    isInvalid={!!invoiceErrors.invoiceDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {invoiceErrors.invoiceDate}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* When a case is attached, move the PRACTICE section here, under the date */}
                {attachedCase ? (
                  <div className="mt-2">{renderPracticeSection()}</div>
                ) : null}

                {/* When NO case is attached, nothing else appears here (keeps original layout) */}
              </Col>
            </Row>
          </Col>
        </Row>

        <hr />

        {/* Action Buttons */}
        <Col md={12} xs={12} sm={12}>
          <div className="d-flex gap-1 mt-2 mb-3 flex-wrap">
            <Button
              variant="outline-primary"
              onClick={handleAddLine}
              className="w-responsive"
              disabled={isPosted}
            >
              + Line
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleAddServiceLine}
              className="w-responsive"
              disabled={isPosted}
            >
              + Service
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleAddTariffLine}
              className="w-responsive"
              disabled={isPosted}
            >
              + Tariff
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleAddNappiLine}
              className="w-responsive"
              disabled={isPosted}
            >
              + Nappi
            </Button>
            <Button
              variant="outline-primary"
              disabled={isPosted}
              onClick={() => {
                // only allow if there is at least one tariff line present (not nappi)
                const hasTariffLine = lines.some(
                  (l) =>
                    (l.type === "tariff" ||
                      !!l.selectedTariff ||
                      !!l.tariffCode) &&
                    !(l.type === "nappi" || !!l.nappiCode)
                );
                if (!hasTariffLine) {
                  alert(
                    "Add a tariff line first. Modifiers cannot be applied to NAPPI lines."
                  );
                  return;
                }
                setShowModifier(true);
              }}
              className="w-responsive"
            >
              + Modifier
            </Button>
          </div>
        </Col>

        {/* Add/Edit Case Modal */}
        <CasePickerPanel
          show={showAddCase}
          onHide={() => setShowAddCase(false)}
          patientId={selectedPatientId}
          existingReferrers={[]}
          currentUserId={selectedPractitionerId}
          invoiceICDSelections={icdSelections}
          onConfirm={async (c) => {
            setAttachedCase(c); // select it
            await refreshCases(selectedPatientId); // <-- make sure list includes the new one
            setShowAddCase(false);

            // keep your existing ICD logic
            let nextSelections = [];
            if (Array.isArray(c.icdSelections) && c.icdSelections.length) {
              nextSelections = c.icdSelections.map((s) => ({
                item: s.item ?? s,
                isPrimary: !!s.isPrimary,
              }));
            } else {
              const primaryItem =
                c.icd101Full || (c.icd101 ? { ICD10_Code: c.icd101 } : null);
              const secondaryItems =
                Array.isArray(c.icd102Full) && c.icd102Full.length
                  ? c.icd102Full
                  : Array.isArray(c.icd102)
                  ? c.icd102.map((code) => ({ ICD10_Code: code }))
                  : [];
              nextSelections = [
                ...(primaryItem
                  ? [{ item: primaryItem, isPrimary: true }]
                  : []),
                ...secondaryItems.map((item) => ({ item, isPrimary: false })),
              ];
            }

            if (nextSelections.length) {
              setIcdSelections(nextSelections);
              setSelectedICD10s(nextSelections.map((s) => s.item));
              setLines((prev) =>
                prev.map((l) =>
                  !l.icd101 && c.icd101 ? { ...l, icd101: c.icd101 } : l
                )
              );
            }
          }}
        />
        {showModifier && (
          <AddModifierModal
            show={showModifier}
            onHide={() => setShowModifier(false)}
            invoiceLines={lines}
            practiceDisciplineCode={disciplineCode || initialDisciplineCode} // ✅ now it's practitioner-driven (with fallback)
            setLines={setLines}
          />
        )}

        {feedback?.overallWarnings &&
          feedback.overallWarnings.code &&
          feedback.overallWarnings.description && (
            <p className="text-warning fw-bold">
              {feedback.overallWarnings.code} -{" "}
              {feedback.overallWarnings.description}
            </p>
          )}

        {feedback?.overallRejections &&
          feedback.overallRejections.code &&
          feedback.overallRejections.description && (
            <p className="text-danger fw-bold">
              Claim Rejected: {feedback.overallRejections.code} -{" "}
              {feedback.overallRejections.description} - Please correct the
              claim and or patient information and re-submit
            </p>
          )}

        {/* Headers */}
        <Col md={12} xs={12} sm={12} className="mb-2">
          <Row className="align-items-center mb-1 g-1">
            <Col md={2} className="col-responsive text-center px-1">
              Appointment | Date
            </Col>
            <Col md={1} className="col-responsive text-center px-1">
              Prim ICD10
            </Col>
            <Col md={1} className="col-responsive text-center px-1">
              Sec ICD10
            </Col>
            <Col md={1} className="col-responsive text-center px-1">
              Tariff | Nappi | (Modifier)
            </Col>
            <Col md={4} className="col-responsive text-center px-1">
              Description
            </Col>
            <Col md={1} className="col-responsive text-center px-1">
              Unit(s)
            </Col>
            <Col md={1} className="col-responsive text-center px-1">
              Rate
            </Col>
            <Col md={1} className="text-center px-1">
              Priv | Del
            </Col>
          </Row>

          {/* Lines */}
          {lines.map((line, index) => (
            <div key={index}>
              {/* Tariff search row */}
              {line.type === "tariff" && !line.selectedTariff && (
                <Row className="align-items-center mt-2 mb-2">
                  <Col md={4}>
                    <TariffSearch
                      practiceNumber={selectedPractice?.practiceNumber}
                      onTariffCodeSelect={(tariff) =>
                        handleTariffLineSelect(index, tariff)
                      }
                    />
                  </Col>
                  <Col md="auto">
                    <Button
                      variant=""
                      onClick={() => handleRemoveLine(index)}
                      className="ps-1 pb-5 pe-4"
                      style={{ width: "33px", height: "33px" }}
                    >
                      <i className="fa fa-trash text-danger" />
                    </Button>
                  </Col>
                </Row>
              )}

              {/* Nappi search row */}
              {line.type === "nappi" && !line.selectedNappi && (
                <Row className="align-items-center mt-2 mb-2">
                  <Col md={4}>
                    <NappiSearch
                      onNappiSelect={(n) => handleNappiLineSelect(index, n)}
                    />
                  </Col>
                  <Col md="auto">
                    <Button
                      variant=""
                      onClick={() => handleRemoveLine(index)}
                      className="ps-1 pb-5 pe-4"
                      style={{ width: "33px", height: "33px" }}
                    >
                      <i className="fa fa-trash text-danger" />
                    </Button>
                  </Col>
                </Row>
              )}

              {/* Service selection row */}
              {line.type === "service" && !line.selectedService && (
                <Row className="align-items-center mb-2">
                  <Col md={4}>
                    <Form.Select
                      size="sm"
                      value={line.serviceId}
                      onChange={(e) =>
                        handleServiceSelect(index, e.target.value)
                      }
                    >
                      <option value="">Select service</option>
                      {services.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.servicesName}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md="auto">
                    <Button
                      variant=""
                      onClick={() => handleRemoveLine(index)}
                      className="p-1 pb-4 pe-4"
                      style={{ width: "30px", height: "30px" }}
                    >
                      <i className="fa fa-trash text-danger" />
                    </Button>
                  </Col>
                </Row>
              )}

              {/* Main row */}
              {(line.type === "custom" ||
                line.selectedService ||
                line.selectedTariff ||
                line.selectedNappi) && (
                <Row className="align-items-center mb-1 g-1">
                  {/* Appt / Date */}
                  <Col md={2}>
                    <>
                      <AppointmentDateInput
                        patientId={line.patientId}
                        fetchAppointments={getAppointmentsByPatientId}
                        initial={lineApptInitial}
                        value={lineApptValue(line)}
                        error={
                          lineErrors?.[index]?.appointmentDate ||
                          lineErrors?.[index]?.appointmentId ||
                          ""
                        }
                        onAppointmentSelect={(sel) => {
                          if (!sel) {
                            const patch = {
                              useCustomDate: false,
                              appointmentId: "",
                              appointmentStartSet: null,
                              appointmentEndSet: null,
                              appointmentDateSet: null,
                              appointmentCustomStart: null,
                              appointmentCustomEnd: null,
                              appointmentDateCustom: null,
                              appointmentDate: null,
                            };

                            handleLineAppointmentChange(index, patch);
                            const gid = lines[index]?.groupId;
                            if (gid) refreshMSRForGroup(gid, patch);
                            else refreshMSRForLine(index, patch);
                            return;
                          }

                          const isNormalized =
                            "appointmentStartSet" in sel ||
                            "appointmentCustomStart" in sel ||
                            "appointmentEndSet" in sel ||
                            "appointmentCustomEnd" in sel;

                          if (isNormalized) {
                            // sel already matches your line fields
                            const patch = sel;

                            handleLineAppointmentChange(index, patch);
                            refreshMSRForLine(index, patch);
                            return;
                          }

                          const toISO = (d, t) => (d && t ? `${d}T${t}` : null);

                          let patch;

                          if (sel.useCustomDate) {
                            patch = {
                              useCustomDate: true,
                              appointmentId: null,
                              appointmentStartSet: null,
                              appointmentEndSet: null,
                              appointmentDateSet: null,
                              appointmentCustomStart: toISO(
                                sel.startDate,
                                sel.startTime
                              ),
                              appointmentCustomEnd: toISO(
                                sel.endDate,
                                sel.endTime
                              ),
                              appointmentDateCustom: sel.startDate || null,
                              appointmentDate: sel.startDate || null,
                            };
                          } else {
                            patch = {
                              useCustomDate: false,
                              appointmentId:
                                sel.appointmentId ||
                                lines[index]?.appointmentId ||
                                "",
                              appointmentStartSet: toISO(
                                sel.startDate,
                                sel.startTime
                              ),
                              appointmentEndSet: toISO(
                                sel.endDate,
                                sel.endTime
                              ),
                              appointmentDateSet: sel.startDate || null,
                              appointmentCustomStart: null,
                              appointmentCustomEnd: null,
                              appointmentDateCustom: null,
                              appointmentDate: sel.startDate || null,
                            };
                          }

                          handleLineAppointmentChange(index, patch);
                          refreshMSRForLine(index, patch);
                        }}
                      />
                      {/* ALWAYS show error message if it exists */}
                      {(lineErrors?.[index]?.appointmentDate ||
                        lineErrors?.[index]?.appointmentId) && (
                        <div className="text-danger small mt-1">
                          {lineErrors?.[index]?.appointmentDate ||
                            lineErrors?.[index]?.appointmentId}
                        </div>
                      )}
                    </>
                  </Col>

                  {/* Primary ICD10 */}
                  <Col md={1} className="px-1 d-flex align-items-center">
                    {primaryIcds.length > 0 && (
                      <Form.Select
                        className="w-100"
                        style={{ Height: "31px" }}
                        value={
                          line.icd101 ||
                          (primaryIcds[0] ? getIcdCode(primaryIcds[0]) : "")
                        }
                        onChange={(e) =>
                          handleLineChange(index, "icd101", e.target.value)
                        }
                        isInvalid={!!lineErrors?.[index]?.icd101}
                      >
                        {primaryIcds.map((icd, idx) => {
                          const codeToUse = getIcdCode(icd);
                          const left = icd.ICD10_3_Code || icd.ICD10_Code || "";
                          const right =
                            icd.ICD10_3_Code && icd.ICD10_Code
                              ? ` | ${icd.ICD10_Code}`
                              : "";
                          return (
                            <option key={idx} value={codeToUse}>
                              {left}
                              {right}
                            </option>
                          );
                        })}
                      </Form.Select>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {lineErrors?.[index]?.icd101}
                    </Form.Control.Feedback>
                  </Col>

                  {/* Secondary ICD10 */}
                  <Col md={1} className="px-1 d-flex align-items-center">
                    <div className="w-100">
                      <CompactMultiSelect
                        id={`sec-icd-${index}`}
                        value={
                          Array.isArray(line.icd10Secondary)
                            ? line.icd10Secondary
                            : []
                        }
                        onChange={(vals) =>
                          handleLineChange(index, "icd10Secondary", vals)
                        }
                        options={secondaryIcds.map((icd) => {
                          const val = getIcdCode(icd);
                          const left = icd.ICD10_3_Code || icd.ICD10_Code || "";
                          const right =
                            icd.ICD10_3_Code && icd.ICD10_Code
                              ? ` | ${icd.ICD10_Code}`
                              : "";
                          return {
                            value: val,
                            label: `${left}${right}`,
                            title: getIcdOneLine(icd),
                          };
                        })}
                      />
                    </div>
                  </Col>

                  {/* Tariff | Nappi | (Modifier) */}
                  <Col md={1} className="col-responsive px-1">
                    {(() => {
                      const isNappi = line.type === "nappi" || !!line.nappiCode;

                      if (isNappi) {
                        return (
                          <Form.Control
                            type="text"
                            className="text-center"
                            size="sm"
                            value={line.nappiCode || ""}
                            onChange={(e) =>
                              handleLineChange(
                                index,
                                "nappiCode",
                                e.target.value
                              )
                            }
                          />
                        );
                      }

                      const modCodes = Array.isArray(line.modifier)
                        ? line.modifier
                            .map((m) => (typeof m === "string" ? m : m.code))
                            .filter(Boolean)
                        : [];
                      const display = modCodes.length
                        ? `${line.tariffCode || ""} (${modCodes.join(", ")})`
                        : line.tariffCode || "";

                      if (modCodes.length) {
                        return (
                          <Form.Control
                            type="text"
                            className="text-center"
                            size="sm"
                            value={display}
                            readOnly
                            title={display}
                            style={{
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          />
                        );
                      }

                      return (
                        <Form.Control
                          type="text"
                          className="text-center"
                          size="sm"
                          value={line.tariffCode || ""}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "tariffCode",
                              e.target.value
                            )
                          }
                        />
                      );
                    })()}
                  </Col>

                  {/* Description */}
                  <Col md={4} className="col-responsive px-1">
                    <Form.Control
                      type="text"
                      size="sm"
                      value={(line.description || "").replace(
                        /\s*\(\d{1,3}\)$/,
                        ""
                      )}
                      title={line.description || ""}
                      onChange={(e) =>
                        handleLineChange(index, "description", e.target.value)
                      }
                      isInvalid={!!lineErrors?.[index]?.description}
                    />
                    <Form.Control.Feedback type="invalid">
                      {lineErrors?.[index]?.description}
                    </Form.Control.Feedback>
                  </Col>

                  {/* Quantity */}
                  <Col md={1} className="col-responsive px-1">
                    <Form.Control
                      type="number"
                      size="sm"
                      className="text-center"
                      value={line.quantity || 1}
                      onChange={(e) =>
                        handleLineChange(index, "quantity", e.target.value)
                      }
                      isInvalid={!!lineErrors?.[index]?.quantity}
                    />
                    <Form.Control.Feedback type="invalid">
                      {lineErrors?.[index]?.quantity}
                    </Form.Control.Feedback>
                  </Col>

                  {/* Rate */}
                  <Col md={1} className="col-responsive px-1">
                    <div className="d-flex align-items-center">
                      <span className="me-1" style={{ fontSize: "0.9rem" }}>
                        R
                      </span>
                      <Form.Control
                        type="text"
                        size="sm"
                        className="text-end flex-grow-1"
                        value={line.rate ?? "0.00"}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const cleaned = raw
                            .replace(",", ".") // treat comma as decimal
                            .replace(/[^0-9.]/g, "");
                          handleLineChange(index, "rate", cleaned);
                        }}
                        onBlur={(e) => {
                          const num = normalizeToNumber(e.target.value);
                          const formatted = num.toFixed(2); // always "." and 2 decimals
                          handleLineChange(index, "rate", formatted);
                        }}
                        readOnly={isPosted}
                        style={isPosted ? { backgroundColor: "#e9ecef" } : {}}
                        isInvalid={!!lineErrors?.[index]?.rate}
                      />
                    </div>
                    <Form.Control.Feedback type="invalid">
                      {lineErrors?.[index]?.rate}
                    </Form.Control.Feedback>
                  </Col>

                  {/* Private & Delete */}
                  <Col md={1} className="col-responsive px-1">
                    <div
                      className="d-flex align-items-center justify-content-end gap-2"
                      style={{ paddingRight: "4px" }}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={line.privatePayment}
                        onChange={(e) =>
                          handleLineChange(
                            index,
                            "privatePayment",
                            e.target.checked
                          )
                        }
                        disabled={isPosted}
                        style={{
                          backgroundColor: isPosted ? "#e9ecef" : "transparent",
                          marginBottom: 0,
                        }}
                      />
                      <Button
                        variant=""
                        onClick={() => handleRemoveLine(index)}
                        className="p-1 pb-4 pe-5"
                        style={{ width: "26px", height: "26px" }}
                        disabled={isPosted}
                      >
                        <i
                          className="fa fa-trash text-danger"
                          style={{ fontSize: "0.9rem" }}
                        />
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Line feedback */}
              {feedback &&
                feedback.lineResults &&
                feedback.lineResults[index] &&
                (feedback.lineResults[index].rejection ? (
                  <p className="text-danger fw-bold">
                    {feedback.lineResults[index]?.status}-{" "}
                    {feedback.lineResults[index].rejection.code} -{" "}
                    {feedback.lineResults[index].rejection.description}
                  </p>
                ) : feedback.lineResults[index].warning ? (
                  <p className="text-warning fw-bold">
                    {feedback.lineResults[index]?.status}-{" "}
                    {feedback.lineResults[index].warning.code} -{" "}
                    {feedback.lineResults[index].warning.description}
                  </p>
                ) : (
                  <p className="text-success fw-bold">
                    {feedback.lineResults[index]?.status}-{" "}
                  </p>
                ))}
            </div>
          ))}
        </Col>

        <hr />

        {/* Totals */}
        <Row className="mt-3">
          <Col md={{ span: 4, offset: 6 }} className="text-end mt-1">
            Total Medical Aid:
          </Col>
          <Col md={2}>
            <Form.Control
              type="text"
              readOnly
              className="text-end"
              style={isPosted ? { backgroundColor: "#e9ecef" } : {}}
              value={formatAmount(totalMedicalAidAmount)}
            />
          </Col>
        </Row>

        <Row className="mt-1">
          <Col md={{ span: 4, offset: 6 }} className="text-end mt-1">
            Total Patient:
          </Col>
          <Col md={2}>
            <Form.Control
              type="text"
              readOnly
              className="text-end"
              style={isPosted ? { backgroundColor: "#e9ecef" } : {}}
              value={formatAmount(totalPatientAmount)}
            />
          </Col>
        </Row>

        <Row className="mt-1">
          <Col md={{ span: 4, offset: 6 }} className="text-end fw-bold mt-1">
            Total:
          </Col>
          <Col md={2}>
            <Form.Control
              type="text"
              readOnly
              className="text-end"
              style={isPosted ? { backgroundColor: "#e9ecef" } : {}}
              value={formatAmount(totalAmount)}
            />
          </Col>
        </Row>
      </main>

      <hr />

      <footer className="mb-5">
        <Row>
          <Col md={6}></Col>
          <Col md={6} className="d-flex justify-content-end gap-2">
            {!invoiceNumber ? (
              <>
                <Button
                  variant="primary"
                  className="w-25"
                  onClick={() =>
                    runGuarded("savePostNew", (start, end) =>
                      saveAndPostNewInvoice(start, end)
                    )
                  }
                  disabled={!!busy}
                >
                  {busy === "savePostNew" ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Posting…
                    </>
                  ) : (
                    "Save & Post"
                  )}
                </Button>

                <Button
                  variant="primary"
                  className="w-25"
                  onClick={() =>
                    runGuarded("saveNew", (start, end) =>
                      saveNewInvoice(start, end)
                    )
                  }
                  disabled={!!busy}
                >
                  {busy === "saveNew" ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving…
                    </>
                  ) : (
                    "Save Invoice"
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="primary"
                  className="w-25"
                  onClick={() =>
                    runGuarded("update", (start, end) =>
                      updateExistingInvoiceAndClaim(start, end)
                    )
                  }
                  disabled={!!busy}
                >
                  {busy === "update" ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-pen-to-square me-1" />
                      Save
                    </>
                  )}
                </Button>

                {invoice?.status === "Pending" && (
                  <Button
                    variant="primary"
                    className="w-25"
                    onClick={() =>
                      runGuarded("post", (start, end) => handlePost(start, end))
                    }
                    disabled={!!busy}
                  >
                    {busy === "post" ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Posting…
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-file-invoice me-1" />
                        Save & Post
                      </>
                    )}
                  </Button>
                )}
              </>
            )}
          </Col>
        </Row>
      </footer>

      {/* ICD10 modal */}
      <ICD10PickerModal
        show={showICDModal}
        onHide={() => setShowICDModal(false)}
        initialSelected={icdSelections}
        onConfirm={(selected) => {
          setShowICDModal(false);

          const mergedICDs = selected.map((s) => {
            const code = getIcdCode(s.item);
            const previous = selectedICD10s.find((old) => {
              const oldCode = old.item ? getIcdCode(old.item) : getIcdCode(old); // fallback if item is flattened
              console.log("oldCode:", oldCode, "code:", code);
              return oldCode === code;
            });

            console.log("old selectedICD10s:", selectedICD10s);
            return {
              item: s.item,
              isPrimary: !!s.isPrimary,
              isPMB: previous?.isPMB ?? s.isPMB ?? false,
              pmbAuthNo: previous?.pmbAuthNo ?? s.pmbAuthNo ?? "",
              pmbStartDate: previous?.pmbStartDate ?? s.pmbStartDate ?? "",
              pmbEndDate: previous?.pmbEndDate ?? s.pmbEndDate ?? "",
              useOnInvoice: previous?.useOnInvoice ?? s.useOnInvoice ?? false,
            };
          });

          // ✅ Update both icdSelections and selectedICD10s with enriched data
          setIcdSelections(
            mergedICDs.map((s) => ({
              item: s.item,
              isPrimary: !!s.isPrimary,
              isPMB: s.isPMB || false,
              pmbAuthNo: s.pmbAuthNo,
              pmbStartDate: s.pmbStartDate,
              pmbEndDate: s.pmbEndDate,
              useOnInvoice: s.useOnInvoice,
            }))
          );

          setSelectedICD10s(mergedICDs);
          // Update lines with the first primary ICD10 code
          const firstPrimaryCode = getIcdCode(
            mergedICDs.find((s) => s.isPrimary)?.item
          );
          console.log("mergedICDs", mergedICDs);
          if (firstPrimaryCode) {
            setLines((prev) =>
              prev.map((line) =>
                !line.icd101 ? { ...line, icd101: firstPrimaryCode } : line
              )
            );
          }
        }}
      />

      {/* Per-line appointment modal (kept as-is for future use) */}
      <InvoiceAppointmentModal
        patientId={
          editingLineIndex != null
            ? lines[editingLineIndex]?.patientId || selectedPatientId
            : selectedPatientId
        }
        initial={lineApptInitial}
        fetchAppointments={getAppointmentsByPatientId}
        onConfirm={(sel) => {
          const patch = buildLineApptPatch(sel);

          if (editingLineIndex != null) {
            setLineFields(editingLineIndex, patch);
            if (editingLineIndex === 0) {
              setDefaultAppointmentTemplate(
                createDefaultTemplate({ ...lines[0], ...patch })
              );
            }
          }
          setShowLineApptModal(false);
        }}
        error={apptErrorFor(editingLineIndex)} // <-- this is the correct prop
        show={showLineApptModal}
        onHide={() => setShowLineApptModal(false)}
      />

      {/* Email modal */}
      {showEmailInvoiceModal && invoiceBlob && (
        <EmailInvoiceModal
          show={showEmailInvoiceModal}
          onHide={() => setShowEmailInvoiceModal(false)}
          defaultEmail={account.accountEmail}
          onSend={async (recipientEmail) => {
            try {
              const result = await sendInvoiceByEmail({
                email: recipientEmail,
                password: account.accountIdNumber,
                pdfBlob: invoiceBlob,
                accountId: account._id,
              });

              if (result.error) {
                throw new Error(result.error);
              }

              console.log("✅ Invoice emailed to", recipientEmail);
            } catch (err) {
              console.error("⛔ Failed to send invoice:", err);
              throw err;
            }
          }}
        />
      )}
    </>
  );
}

export default CreateInvoiceModal;
